// Shared scan-flow component for Shelf Drop & Forecast Drop pages.
//
// The two modules share the same UX — the only differences are:
//   - which BE module flag we pass ('shelf' | 'forecast')
//   - whether the filter pills row is shown (Shelf Drop only)
//   - default page title (passed in by the page wrapper)
//
// Phase machine:
//
//   awaiting-starting-location  → awaiting-tote  → awaiting-location
//                                                         ↓
//                                                  awaiting-items
//                                                         ↓
//                                          /drop/pick-complete
//                                                         ↓
//                                          (loop, or "all caught up")
//
// awaiting-starting-location is always required — the BE needs the
// picker's anchor to rank target locations by proximity.
// awaiting-location is skipped when the warehouse setting
// picking_require_location_scan is off; the flow goes tote → items
// and the picker scans the item barcode directly. The target
// location's barcode is still accepted in awaiting-items as an
// idempotent no-op for pickers who scan it out of habit.
//
// Reuses HiddenScanInput (scan-panel direct child per Portal CSS rule),
// playBoxAdd / playSuccess / playToteFinalised / playError audio.
//
// The page-title-bar toggle flips this same component into the
// DropPutAwayView sub-view via the `view` state.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../helpers/showToast";
import { ArrowLeft, PackageOpen, AlertTriangle, Info, MapPin, X } from "lucide-react";
import ScanPromptHero from "../picking-shared/ScanPromptHero";
import ScanFlashOverlay from "../ScanFlashOverlay";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playToteFinalised,
} from "../helpers/audioPlayer";
import { useDemoVisible } from "../helpers/demoMode";
import { useOrderCutoff } from "../helpers/useOrderCutoff";
import DropFilterPills, {
    dropWireFilters, type DropFilters, type DropChannel, type DropAge,
} from "./DropFilterPills";
import DropPutAwayView from "./DropPutAwayView";
import DropViewToggle from "./DropViewToggle";
import PickingScreen, { type LastPickedItem } from "../picking-shared/PickingScreen";
import ManualPickModal from "../picking-shared/ManualPickModal";
import PickingTopBar from "../picking-shared/PickingTopBar";
import { usePickSpeed } from "../picking-shared/usePickSpeed";
import { useCycleScore } from "../gamification";
import FlowLoader from "../FlowLoader";
// Missing-item count flow — same screens as picking's missing flow (the
// count is a real stock check either way), so the components are shared.
import MissingLocationGate from "../picking/MissingLocationGate";
import MissingItemCountList, { type StockCheckLine } from "../picking/MissingItemCountList";
import MissingFinishConfirm, { type UncountedLine } from "../picking/MissingFinishConfirm";
// The count list renders the Stock Check module's row classes.
import "../../styles/stock-check.css";
// Reopen-tote modal reuses book-in's modal/card styles (.book-in-reopen-modal,
// .book-in-close-card*, .book-in-close-hint). book-in.css is already in the
// global bundle (the demo panel uses .book-in-demo-panel), but import it
// explicitly so the dependency is clear and survives any future code-split.
import "../../styles/book-in.css";

// Demo barcodes match the rows seeded by `seedPickingDemo` in
// maintenance.controller.js. Keep these in sync with the seed.
const DEMO_LOW_LOCATIONS  = ["NP-A-1-1-1", "NP-A-1-1-2", "NP-A-1-2-1", "NP-A-1-2-2", "NP-A-2-1-1"];
const DEMO_HIGH_LOCATIONS = ["NP-H-A-1-1-1", "NP-H-A-1-1-2", "NP-H-A-1-2-1", "NP-H-A-1-2-2", "NP-H-A-2-1-1"];
const DEMO_TOTES = ["10000", "10001", "10002", "10003", "10004", "10005", "10006", "10007"];

export type DropModule = "shelf" | "forecast";

export type Phase =
    | "awaiting-starting-location"
    | "awaiting-tote"
    | "awaiting-location"
    | "awaiting-items"
    // Missing-item count flow (flag button) — mirrors picking's phases:
    // forced location-scan gate → live count list → outcome card.
    | "missing-location"
    | "missing-counting"
    | "missing-resolved"
    | "complete";

type ScanStatus = "idle" | "success" | "error" | "complete";

interface DropItem {
    sku: string;
    qty: number;
    productName?: string;
    productBarcode?: string;
    barcodes?: string[];
    // Catalogue thumbnail (fast-load) + full image (fallback) — drives
    // the PickingScreen product card. The BE returns these from
    // findNextLocation (drop-needs.js).
    productImageUrl?: string | null;
    productThumbnailUrl?: string | null;
}
interface DropLocation {
    location: string;
    // Short aisle-bay-level label, like Pick Queue's `location_display`.
    // Pickers see this in the hero so the value fits on one line; the
    // full barcode is still used for validation against scans.
    locationDisplay?: string;
    walkOrder: number;
    items: DropItem[];
    // Visual context for the unified PickingScreen — bay-position
    // diagram + green location-scan accent only apply when both fields
    // are set and locationHighLow === "low".
    locationHighLow?: "high" | "low" | null;
    bayPosition?: string | null;
}

interface DropSession {
    module: DropModule;
    toteId: string | null;
    toteUnits?: number;
    walkOrder: number | null;
    filters: DropFilters;
    currentLocation: string | null;
    target: DropLocation | null;
    progress: unknown;
    startedAt: string;
    lastActivityAt: string;
}

// Missing-item flow session state. Held while the picker counts a location
// whose stock wasn't physically there (phases missing-location /
// missing-counting). The backing check is a real stock_checks row
// (origin='drop_missing') opened by /drop/missing/start — unlike picking
// there is no legacy no-check fallback: if the check can't open, the flag
// degrades to the old skip and this state is never set.
interface DropMissingState {
    /** The hero SKU that sent them into the flow. */
    targetSku: string;
    /** Units of it still owed at this location when flagged. */
    targetQty: number;
    /** Location barcode (validation) + display name (screen). */
    location: string;
    locationDisplayName: string;
    checkId: number;
    lines: StockCheckLine[];
}
// Outcome of the count, shown on the missing-resolved card.
interface DropMissingResolved {
    sku: string;
    foundQty: number;
    requiredQty: number;
    writtenOff: number;
}
// A count scan of a real product this location doesn't stock, awaiting the
// picker's "book it in?" decision. `extraUnit` distinguishes a further unit
// of a SKU the count already knows about (surplus / already_added) from a
// SKU that isn't recorded here at all — the modal wording differs.
interface DropSurplusPrompt {
    sku: string;
    barcode: string;
    extraUnit: boolean;
}

type ResumeAction = "awaiting-tote" | "awaiting-location" | "awaiting-items" | "advance";

export const deriveDropResumeAction = (
    session: Pick<DropSession, "toteId" | "target">,
    requireLocationScan: boolean,
): ResumeAction => {
    if (!session.toteId) return "awaiting-tote";
    if (!session.target) return "advance";
    return requireLocationScan ? "awaiting-location" : "awaiting-items";
};

export const remainingAfterGrantedUnits = (
    remaining: number,
    requested: number,
    granted: number,
): number => Math.max(0, remaining - Math.min(requested, Math.max(0, granted)));

// Older servers do not include toteUnits, so retain support for both known
// progress shapes as a resume-count fallback.
export const countDropSessionUnits = (progress: unknown): number => {
    if (typeof progress === "number") return Math.max(0, progress);
    if (Array.isArray(progress)) {
        return progress.reduce<number>((sum, entry) => sum + countDropSessionUnits(entry), 0);
    }
    if (!progress || typeof progress !== "object") return 0;
    const record = progress as Record<string, unknown>;
    if (typeof record.qty === "number") return Math.max(0, record.qty);
    return Object.values(record).reduce<number>((sum, value) => sum + countDropSessionUnits(value), 0);
};

export const getDropSessionUnitCount = (
    session: Pick<DropSession, "toteUnits" | "progress">,
): number => typeof session.toteUnits === "number"
    ? session.toteUnits
    : countDropSessionUnits(session.progress);

// The last location the session's picker got an item from, for the
// close-tote card on the resume path. The BE only ever writes
// current_location from an accepted /drop/scan-item (session start and
// move NULL it), so it is exactly that — not merely where they anchored.
// Prefer the target's display name when it names the same location;
// otherwise the raw barcode is still the label the picker scanned.
export const deriveSessionLastLocation = (
    session: Pick<DropSession, "currentLocation" | "target">,
): string | null => {
    if (!session.currentLocation) return null;
    if (session.target && session.target.location === session.currentLocation) {
        return session.target.locationDisplay ?? session.target.location;
    }
    return session.currentLocation;
};

export const isVacuousDropSession = (
    session: Pick<DropSession, "toteId" | "target" | "toteUnits" | "progress">,
): boolean => {
    const progressUnits = countDropSessionUnits(session.progress);
    const toteUnits = typeof session.toteUnits === "number" ? session.toteUnits : progressUnits;
    return session.toteId === null
        && session.target === null
        && toteUnits === 0
        && progressUnits === 0;
};

export const shouldPlayOptimisticDropFeedback = (
    optimisticTotalLeft: number,
    queuedLocationCount: number,
): boolean => optimisticTotalLeft > 0 || queuedLocationCount > 0;

// Per-SKU units the picker is already being sent to get: the un-scanned
// remainder at the current target plus every queued stop's quantities.
// Sent to /drop/next as `pendingPicks` on queue refills — un-scanned
// units have no drop_claims yet, so without this the BE re-counts their
// need and tops the queue up with ANOTHER location for a SKU these
// stops already cover (the phantom "get another" hero flicker).
export const plannedDropPicks = (
    locs: { items: { sku: string; qty: number }[] }[],
    remaining?: Record<string, number>,
): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const l of locs) {
        for (const it of l.items) out[it.sku] = (out[it.sku] ?? 0) + it.qty;
    }
    if (remaining) {
        for (const [sku, qty] of Object.entries(remaining)) {
            if (qty > 0) out[sku] = (out[sku] ?? 0) + qty;
        }
    }
    return out;
};

interface DropSettings {
    require_location_scan: boolean;
    lookahead_orders?: number;
    /** Picker hero — show/hide product image. Defaults to true. */
    show_product_image?: boolean;
    /** "Also at this location" panel — 0-10 rows. Default 1. */
    other_skus_count?: number;
    /** Location-change takeover duration (ms). Default 2000; 0 disables. */
    location_change_ms?: number;
    /** "New location" highlight duration (ms). Default 6000; 0 disables. */
    new_location_highlight_ms?: number;
}

interface Props {
    module: DropModule;
    titleKey: string; // i18n key for page title
}

const QUEUE_POLL_MS = 5000;
// Re-check the 13:00 London dispatch cut-off once a minute so the
// "must ship today" clock toggle appears on a screen that has been
// sitting open since the morning (same cadence as the Pick Queue).

// The signed-in operator's site. Points targets are per warehouse and the
// drop session payload doesn't carry one.
const dropWarehouse = (): string | undefined => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return undefined;
        const parsed = JSON.parse(raw);
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : undefined;
    } catch { return undefined; }
};

// Last-applied channel/age filter, remembered per module so leaving the
// page and coming back (put-away view, home, a reload) re-applies what the
// picker chose — including a Custom date/time range. The stored value only
// SEEDS the state on mount; a resumed BE session's own filters still win
// (handleResume overwrites, and the mirror effect then stores those).
const filtersStoreKey = (module: DropModule) => `drop-filters:${module}`;
const loadStoredFilters = (module: DropModule): DropFilters => {
    const fallback: DropFilters = { channel: "all", age: "all" };
    try {
        const raw = window.localStorage.getItem(filtersStoreKey(module));
        if (!raw) return fallback;
        const p = JSON.parse(raw);
        const channels: DropChannel[] = ["all", "express", "standard"];
        const ages: DropAge[] = ["all", "<1", "1-2", "2+", "custom"];
        const opt = (v: unknown) => (typeof v === "string" && v ? v : undefined);
        return {
            channel: channels.includes(p?.channel) ? p.channel : "all",
            age: ages.includes(p?.age) ? p.age : "all",
            ageFrom: opt(p?.ageFrom),
            ageTo: opt(p?.ageTo),
            ageFromTime: opt(p?.ageFromTime),
            ageToTime: opt(p?.ageToTime),
            due: p?.due === true ? true : undefined,
        };
    } catch { return fallback; }
};

// Reconcile a freshly-read server progress reading into the displayed
// state. The bar is shared + server-authoritative, but it must NEVER move
// backwards (hard UX guarantee) — so every update path funnels through
// here, whether it's the 5s background poll or the imperative refresh
// fired right after a /drop/pick-complete.
//
//   • total === 0 is the BE's drained-cycle signal (getProgress returns
//     {0,0} only when remaining + pickedSinceCycle === 0, i.e. the module
//     is genuinely idle — this cannot happen mid-cycle once anything has
//     been picked). That is the one legitimate reset: snap to 0/0 so the
//     next batch fills from empty.
//   • Otherwise clamp picked and total to their running maxima. A stale
//     read, a concurrent picker's cancel (which raises `remaining` and so
//     lowers the BE's picked = peak − remaining), or a rolled-back
//     optimistic pick can then only ever STALL the bar, never rewind it.
//
// `total` is also kept ≥ `picked` so the fraction stays valid.
const mergeProgress = (
    prev: { total: number; picked: number },
    d: { total: number; picked: number },
): { total: number; picked: number } => {
    if (d.total === 0) return { total: 0, picked: 0 };
    const picked = Math.max(prev.picked, d.picked);
    const total = Math.max(prev.total, d.total, picked);
    return { total, picked };
};

const ActiveDropScreen = ({ module, titleKey }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // ─── view: scan vs put-away ──────────────────────────────────
    // ?view=put-away opens straight into the put-away sub-view — the
    // wrong-queue prompt on another put-away screen deep-links here.
    const location = useLocation();
    const [view, setView] = useState<"scan" | "put-away">(() =>
        new URLSearchParams(location.search).get("view") === "put-away" ? "put-away" : "scan");
    const [putAwayCount, setPutAwayCount] = useState(0);
    const [itemsCount, setItemsCount] = useState(0);
    // Shared module-wide progress (across all pickers). Drives the
    // progress bar fill + the X-of-Y counter in the active-pick
    // header. Server-authoritative — see drop-progress.js.
    const [progress, setProgress] = useState<{ total: number; picked: number }>({ total: 0, picked: 0 });
    // Channel/age filter (Shelf Drop only). Declared up here — above the
    // scan-side state it conceptually belongs with — because the units
    // badge poll below sends it, so the top-of-page count agrees with
    // the applied filter.
    const [filters, setFilters] = useState<DropFilters>(() => loadStoredFilters(module));

    // Mirror every filter change back to storage — the pill taps, the
    // session's filters on resume, and the reset on discard — so the next
    // visit to this page starts from the last applied state.
    useEffect(() => {
        try {
            window.localStorage.setItem(filtersStoreKey(module), JSON.stringify(filters));
        } catch { /* storage blocked/full — the filter just won't persist */ }
    }, [module, filters]);

    // "Must ship today" clock toggle gate. The stored `filters.due`
    // choice only renders / applies after today's order cut-off for this
    // warehouse (Settings › Dispatch › Channel cut-offs, read by the hook)
    // — dropWireFilters re-derives that at every request, and this state
    // is what re-runs the badge + aisle reads when the cut-off flips
    // while the page is open.
    const { afterCutoff, cutoff } = useOrderCutoff();

    // Poll the put-away + items counters regardless of which view is
    // open so both toggle badges stay current. The items count carries
    // the picker's channel/age filter (Shelf only) so the number at the
    // top of the screen matches the work the filter would send them to;
    // re-runs immediately on any filter change.
    useEffect(() => {
        let alive = true;
        const fetchCount = async () => {
            try {
                const params = new URLSearchParams({ module });
                if (module === "shelf") {
                    const wf = dropWireFilters(filters, Date.now(), cutoff);
                    if (wf.channel !== "all") params.set("channel", wf.channel);
                    if (wf.age !== "all")     params.set("age", wf.age);
                    if (wf.ageFrom)           params.set("ageFrom", wf.ageFrom);
                    if (wf.ageTo)             params.set("ageTo", wf.ageTo);
                    if (wf.due)               params.set("due", "1");
                }
                const res = await apiFetch(`/drop/queue-count?${params.toString()}`);
                if (!res.ok || !alive) return;
                const d = await res.json();
                if (typeof d.awaitingPutAway === "number") setPutAwayCount(d.awaitingPutAway);
                if (typeof d.count === "number") setItemsCount(d.count);
            } catch { /* silent — next tick retries */ }
        };
        fetchCount();
        const id = setInterval(fetchCount, QUEUE_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [module, filters, afterCutoff]);

    // ─── units-by-aisle popup ────────────────────────────────────
    // On-demand breakdown of where the outstanding units will be
    // picked from, so a supervisor can spread pickers across the busy
    // aisles. Fetched when the info button is pressed (not polled);
    // carries the same channel/age filters as the units badge so the
    // per-aisle figures sum to the badge count.
    type AisleRow = { aisle: string | null; units: number };
    const [aisleModalOpen, setAisleModalOpen] = useState(false);
    // null = loading; [] = nothing outstanding.
    const [aisleRows, setAisleRows] = useState<AisleRow[] | null>(null);
    const openAisleModal = useCallback(async () => {
        setAisleModalOpen(true);
        setAisleRows(null);
        try {
            const params = new URLSearchParams({ module });
            if (module === "shelf") {
                const wf = dropWireFilters(filters, Date.now(), cutoff);
                if (wf.channel !== "all") params.set("channel", wf.channel);
                if (wf.age !== "all")     params.set("age", wf.age);
                if (wf.ageFrom)           params.set("ageFrom", wf.ageFrom);
                if (wf.ageTo)             params.set("ageTo", wf.ageTo);
                if (wf.due)               params.set("due", "1");
            }
            const res = await apiFetch(`/drop/aisles?${params.toString()}`);
            const d = res.ok ? await res.json() : null;
            setAisleRows(Array.isArray(d?.aisles) ? d.aisles : []);
        } catch {
            setAisleRows([]);
        }
    }, [module, filters, afterCutoff]);

    // Shared module-wide progress fetch. Polled on a timer so other
    // pickers' contributions show up here, and called imperatively from
    // submitScan after pick-complete so the bar reconciles immediately
    // instead of waiting up to QUEUE_POLL_MS. Every reading is folded in
    // via mergeProgress, which is forward-only — the bar can stall but
    // never rewind. See mergeProgress for the drained-cycle reset.
    const refreshProgress = useCallback(async () => {
        try {
            const res = await apiFetch(`/drop/progress?module=${module}`);
            if (!res.ok) return;
            const d = await res.json();
            if (typeof d.total === "number" && typeof d.picked === "number") {
                setProgress(prev => mergeProgress(prev, d));
            }
        } catch { /* silent — next tick retries */ }
    }, [module]);
    useEffect(() => {
        let alive = true;
        const tick = async () => { if (alive) await refreshProgress(); };
        tick();
        const id = setInterval(tick, QUEUE_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [refreshProgress]);

    // ─── scan-side state ─────────────────────────────────────────
    const [phase, setPhase] = useState<Phase>("awaiting-starting-location");
    const [walkOrder, setWalkOrder] = useState<number | null>(null);
    // The barcode of the location the picker is physically at (last
    // scanned anchor / current target). Sent to /drop/next so the BE
    // can apply its same-bay and same-aisle ranking rules — walk_order
    // alone can't express "still in this aisle" (and isn't unique).
    const currentLocRef = useRef<string | null>(null);
    const [toteId, setToteId] = useState<string | null>(null);
    // Display-only count for the tote currently in the picker's hand.
    // The BE remains authoritative; this local total exists so the
    // close-confirm card can immediately show what the picker scanned.
    const [toteUnits, setToteUnits] = useState(0);
    // Reopen-closed-tote prompt. Set when /drop/tote/start (mode
    // "start") or a mid-pick /drop/tote/swap (mode "swap") reports the
    // scanned tote is 'closed' (holds picked stock awaiting put-away).
    // Confirming flips it back to 'open' keeping its contents so the
    // picker can resume adding — via /drop/tote/reopen for "start", or
    // /drop/tote/swap with reopen:true for "swap" (which also commits
    // the current tote's partial picks). Mirrors the book-in reopen
    // flow (continue-only — no "empty", since the stock is physically
    // in the tote).
    type ReopenPrompt = { toteId: string; units: number; lines: number; mode: "start" | "swap" };
    const [reopenPrompt, setReopenPrompt] = useState<ReopenPrompt | null>(null);
    const [reopening, setReopening] = useState(false);
    // Ref mirror so the scan handler (a stable-ish closure) always sees
    // the current prompt when deciding whether a scan confirms reopen.
    const reopenPromptRef = useRef<ReopenPrompt | null>(null);
    useEffect(() => { reopenPromptRef.current = reopenPrompt; }, [reopenPrompt]);
    // Confirm-swap prompt. A mid-pick scan of a FREE tote no longer
    // swaps on the spot: /drop/tote/swap is first called with check:true
    // (validation only) and, if the tote is swappable, this prompt asks
    // the picker to confirm. Rescanning the same tote or tapping Confirm
    // performs the real swap; anything else is rejected while it's open.
    // Added after 2026-09-03 tote 5023: a stray tote-label scan silently
    // switched totes and stranded 10 units in a tote nobody had in hand.
    type SwapPrompt = { toteId: string };
    const [swapPrompt, setSwapPrompt] = useState<SwapPrompt | null>(null);
    const [swapping, setSwapping] = useState(false);
    const swapPromptRef = useRef<SwapPrompt | null>(null);
    useEffect(() => { swapPromptRef.current = swapPrompt; }, [swapPrompt]);
    const swappingRef = useRef(false);
    // Synchronous in-flight guard for reopen — a rescan burst can fire
    // handleReopen again before the `reopening` state flush, which would
    // send a second /drop/tote/reopen. The ref blocks it immediately.
    const reopeningRef = useRef(false);
    // Close-current-tote prompt. One scan only opens this confirmation;
    // the same tote must be scanned again (or Confirm tapped) before the
    // durable session is completed and the tote is settled for put-away.
    // lastLocation = where the picker last got an item (display name),
    // shown on the card so they know where they were when they stop.
    type ClosePrompt = { toteId: string; units: number; lastLocation: string | null };
    const [closePrompt, setClosePrompt] = useState<ClosePrompt | null>(null);
    const [closing, setClosing] = useState(false);
    const closePromptRef = useRef<ClosePrompt | null>(null);
    useEffect(() => { closePromptRef.current = closePrompt; }, [closePrompt]);
    // Prompt scans can arrive before React commits the state render, so
    // update the ref synchronously at every open/dismiss call as well.
    const updateClosePrompt = useCallback((prompt: ClosePrompt | null) => {
        closePromptRef.current = prompt;
        setClosePrompt(prompt);
    }, []);
    const closingRef = useRef(false);
    // Move-to-location prompt. Scans are intercepted while it is open and
    // treated as the new anchor location, leaving the active target mounted
    // until the server accepts the move and the fresh target arrives.
    type MovePrompt = { open: true };
    const [movePrompt, setMovePrompt] = useState<MovePrompt | null>(null);
    const [moving, setMoving] = useState(false);
    const movePromptRef = useRef<MovePrompt | null>(null);
    useEffect(() => { movePromptRef.current = movePrompt; }, [movePrompt]);
    const updateMovePrompt = useCallback((prompt: MovePrompt | null) => {
        movePromptRef.current = prompt;
        setMovePrompt(prompt);
    }, []);
    // Active server session found during startup. The initial loader stays
    // visible until this lookup resolves, then this prompt sits over the
    // normal scan screen until the picker resumes or abandons it.
    const [resumePrompt, setResumePrompt] = useState<DropSession | null>(null);
    const [sessionChecked, setSessionChecked] = useState(false);
    const [resuming, setResuming] = useState(false);
    const resumePromptRef = useRef<DropSession | null>(null);
    useEffect(() => { resumePromptRef.current = resumePrompt; }, [resumePrompt]);
    const resumingRef = useRef(false);
    // Stashed session when the close-confirm was opened FROM the resume modal
    // ("Close tote"). Cancelling that confirm — or a close that fails — must put
    // the operator back on the resume modal: they are holding a tote, and
    // dropping them on the start screen with a live server session and no
    // Resume option is a dead end. Stays null when the confirm was opened
    // mid-pick, where dismissing correctly returns to the live pick.
    const resumeAwaitingCloseRef = useRef<DropSession | null>(null);
    // Dismiss the close-confirm, restoring the resume modal when that is where
    // it came from and the tote was NOT closed. Used by every dismiss path
    // (Cancel, the X, the overlay) and by handleCloseTote's failure branches.
    const dismissClosePrompt = useCallback(() => {
        const pending = resumeAwaitingCloseRef.current;
        resumeAwaitingCloseRef.current = null;
        closePromptRef.current = null;
        setClosePrompt(null);
        if (pending) {
            resumePromptRef.current = pending;
            setResumePrompt(pending);
        }
    }, []);

    // A tote owned by another picker's unfinished Drop requires an explicit
    // takeover before it can re-enter the normal tote lifecycle.
    type TakeoverPrompt = { toteId: string; units: number; lines: number; pickerName: string };
    const [takeoverPrompt, setTakeoverPrompt] = useState<TakeoverPrompt | null>(null);
    const [takingOver, setTakingOver] = useState(false);
    const takeoverPromptRef = useRef<TakeoverPrompt | null>(null);
    useEffect(() => { takeoverPromptRef.current = takeoverPrompt; }, [takeoverPrompt]);
    const takingOverRef = useRef(false);
    const [target, setTarget] = useState<DropLocation | null>(null);
    // Multi-location lookahead. The BE returns the current target +
    // (settings.picking_lookahead_orders − 1) further locations in
    // one response so the FE keeps a queue. Each pick-complete pops
    // the front, swaps the hero instantly, and refills the queue
    // asynchronously — the picker never sees "Finding next…".
    const [lookaheadQueue, setLookaheadQueue] = useState<DropLocation[]>([]);
    const [lookaheadDepth, setLookaheadDepth] = useState<number>(2);
    // Per-target-location scan toggle. Driven by the picking
    // require_location_scan setting (one source of truth across picking
    // and drops). When false, the awaiting-location phase is skipped
    // after a tote scan — picker advances straight to awaiting-items
    // and scans the item directly. The location barcode is still
    // accepted in awaiting-items as an idempotent no-op so habit
    // scans don't fail. Starting-location scan is always required
    // independent of this — the BE needs the picker's anchor walk_order.
    const [requireLocScan, setRequireLocScan] = useState<boolean>(true);
    const [showProductImage, setShowProductImage] = useState<boolean>(true);
    const [otherSkusCount, setOtherSkusCount] = useState<number>(1);
    // Bumped to suppress PickingScreen's location-change transition for one
    // corrective hero move (a failed pick-complete rolling back to the location
    // the picker never left). See PickingScreen's suppressNextTransition prop.
    const [suppressTransition, setSuppressTransition] = useState(0);
    // SKU last rendered as the hero card. Used only to hold that same product on
    // screen while a satisfied location waits for its pick-complete round-trip
    // (see `currentItem`) — a render-scoped cache, never read for pick logic.
    const heroSkuRef = useRef<string | null>(null);
    const [locationChangeMs, setLocationChangeMs] = useState<number | undefined>(undefined);
    const [newLocationHighlightMs, setNewLocationHighlightMs] = useState<number | undefined>(undefined);
    // Pick-rate counter — fed by the drop-side advance hook so the
    // bottom action bar's items/hr stays accurate across pick-completes.
    const { rate: pickSpeed, recordPick } = usePickSpeed();

    // Trophy points, scan-to-scan like picking. Shelf and Forecast get
    // separate targets: a forecast drop walks a different route with a
    // different unit size, so one number for both would flatter one of them.
    const cycle = useCycleScore(
        dropWarehouse(),
        module === "forecast" ? "drop_timer_forecast_ms" : "drop_timer_shelf_ms",
        `drop-best-time-${module}`,
    );
    const cycleRef = useRef(cycle);
    cycleRef.current = cycle;
    // Per-SKU remaining counter at the current location.
    const [remainingBySku, setRemainingBySku] = useState<Record<string, number>>({});
    // The unit the picker most recently dropped — drives the green
    // banner at the bottom of PickingScreen (labelled "Last dropped"
    // here). Null until the first accepted item scan; deliberately
    // survives the "all caught up" → next-batch transition since the
    // component stays mounted.
    const [lastDropped, setLastDropped] = useState<LastPickedItem | null>(null);
    // Display name of the location the picker last got an item from.
    // Shown on the close-tote card so they know where they were when
    // they stop. Unlike lastDropped it deliberately SURVIVES a tote
    // swap — it tracks the picker's physical trail, not tote contents.
    const [lastDropLocation, setLastDropLocation] = useState<string | null>(null);
    // The CLOSED session's last item-pick location, carried across the
    // tote close so the "scan current location" screen that follows can
    // still tell the picker where they were. lastDropLocation itself is
    // wiped with the rest of the session state on close; this survives
    // until they anchor a new starting location (or enter a session via
    // resume/abandon, which makes the old trail stale).
    const [postCloseLastLocation, setPostCloseLastLocation] = useState<string | null>(null);
    // Missing-item count flow (flag button). `missing` lives through the
    // gate + counting phases; `resolved` through the outcome card.
    const [missing, setMissing] = useState<DropMissingState | null>(null);
    const [resolved, setResolved] = useState<DropMissingResolved | null>(null);
    const [surplusPrompt, setSurplusPrompt] = useState<DropSurplusPrompt | null>(null);
    const [addingSurplus, setAddingSurplus] = useState(false);
    const [finishConfirm, setFinishConfirm] = useState(false);
    // Synchronous mirrors for the scan handler — same pattern as
    // resumePromptRef / closePromptRef / movePromptRef: a scan can arrive
    // before React commits the state render (and before the effect that
    // re-points HiddenScanInput's onSubmit ref at the fresh closure), so
    // scan interception must never depend on closure freshness. State
    // drives the render; these refs drive the dispatch.
    const missingRef = useRef<DropMissingState | null>(null);
    const missingStageRef = useRef<"gate" | "counting" | null>(null);
    const [scanValue, setScanValue] = useState("");
    const [scanStatus, setScanStatus] = useState<ScanStatus>("idle");
    const [submitting, setSubmitting] = useState(false);
    // When the module is fully replenished we first play the amber
    // complete-tick (via scanStatus="complete"), then after it fades
    // swap to the "all caught up" message. `completeStage` tracks
    // which half of that sequence we're rendering.
    //
    // The tick only plays when the picker actively *finished* the
    // last item (a "you just did it!" cue). When the page is opened
    // and the module is already caught up, we want to skip the tick
    // and land directly on the resting state. The ref below is set
    // to true at the pick-complete call sites and consumed by the
    // useEffect below — refs (not state) so we don't trigger an
    // extra render between setPhase and the effect.
    const [completeStage, setCompleteStage] = useState<"tick" | "message">("message");
    const playTickOnNextCompleteRef = useRef(false);
    // True once the first /drop/next response has resolved. Until then
    // we render nothing in the hero slot so the picker never sees
    // "scan current location" flash before "all caught up" (when the
    // module is already drained on page open).
    const [initialized, setInitialized] = useState(false);
    const demo = useDemoVisible();

    const flashTimerRef = useRef<number | null>(null);
    const flash = useCallback((status: Exclude<ScanStatus, "idle">) => {
        setScanStatus(status);
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => setScanStatus("idle"), 900) as unknown as number;
    }, []);
    useEffect(() => () => {
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    }, []);

    // Scan-burst queue. A fast scanner can fire the next barcode before
    // the server has answered the previous one — without a queue those
    // extras get dropped (HiddenScanInput's global keydown listener
    // short-circuits while `disabled` is true, and submitScan itself
    // bails on `submitting`). Same pattern as book-in.tsx and the
    // Pick Queue active screen: track the in-flight call via a ref,
    // stash overflow barcodes, drain one per `finally` via setTimeout(0)
    // so React can commit state updates between calls.
    const inFlightRef = useRef(false);
    const scanQueueRef = useRef<string[]>([]);
    // Dedup: hardware scanners commonly fire the SAME barcode twice
    // within ~30-50ms when the trigger is held or the read retries.
    // Without dedup, after the first scan completed the location
    // (last item → optimistic swap to the next target), the duplicate
    // lands on the new target's awaiting-location phase and fails with
    // `drop_wrong_location`. 250ms is well under the physical pace of
    // legitimate scanning (~500ms per pick + aim + fire) so legit fast
    // bursts are not dropped. `performance.now()` is monotonic, so
    // wall-clock jumps can't widen the window.
    const lastScanRef = useRef<{ barcode: string; at: number } | null>(null);
    const DEDUP_WINDOW_MS = 250;
    // submitScan recurses into itself from `finally` via this ref so the
    // drain always invokes the latest closure (deps re-create the cb).
    const submitScanRef = useRef<(raw?: string) => void>(() => {});
    // The full target travels only with the first /drop/scan-item request at
    // each location. A failed request restores this ref so retry still sends
    // the target; a resumed target is already known to the server.
    const lastScanItemLocationRef = useRef<string | null>(null);

    // When the module flips to "complete":
    //   • Picker just finished the last item → play the amber tick
    //     for 750 ms, then swap to the "ALL CAUGHT UP" message.
    //   • Page opened with nothing to do (preload-empty / filter
    //     change wipes the queue) → skip the tick and land on the
    //     message directly. The tick is a "you just did it!" cue
    //     and shouldn't fire when the picker didn't just do anything.
    useEffect(() => {
        if (phase !== "complete") {
            // Reset for the next entry into complete.
            setCompleteStage("message");
            return;
        }
        const playTick = playTickOnNextCompleteRef.current;
        playTickOnNextCompleteRef.current = false;
        if (!playTick) {
            setCompleteStage("message");
            setScanStatus("idle");
            return;
        }
        // completeStage="tick" and scanStatus="complete" are set
        // synchronously by the call site that flips phase=complete,
        // so the first paint already shows the amber tick (no
        // one-frame "ALL CAUGHT UP" flash in between).
        //
        // Cancel any pending success-flash timer — its 900 ms
        // setScanStatus("idle") would otherwise race the 750 ms
        // tick → message swap and blank the tick a frame early.
        if (flashTimerRef.current) {
            window.clearTimeout(flashTimerRef.current);
            flashTimerRef.current = null;
        }
        const t = window.setTimeout(() => {
            setScanStatus("idle");
            setCompleteStage("message");
        }, 750);
        return () => window.clearTimeout(t);
    }, [phase]);

    // Resolve any per-picker server session alongside settings and the
    // existing /drop/next preload. The render gate below waits for both the
    // preload and this lookup, so the fresh screen never flashes before the
    // resume choice is known.
    // Fetch this picker's live server session for the module, or null when
    // there is none worth resuming. Shared by the mount lookup and the
    // session/start refusal path (active_tote_session), so both put the
    // same resume choice in front of the picker.
    const lookupActiveSession = useCallback(async (): Promise<DropSession | null> => {
        try {
            const res = await apiFetch(`/drop/session/active?module=${module}`);
            if (!res.ok) return null;
            const body = await res.json();
            if (body.ok && body.session) {
                const session = body.session as DropSession;
                if (!isVacuousDropSession(session)) return session;
            }
        } catch { /* a failed lookup falls back to the fresh flow */ }
        return null;
    }, [module]);

    useEffect(() => {
        let alive = true;
        setSessionChecked(false);
        setResumePrompt(null);
        (async () => {
            try {
                const session = await lookupActiveSession();
                if (alive && session) {
                    setLookaheadQueue([]);
                    setResumePrompt(session);
                }
            } finally {
                if (alive) setSessionChecked(true);
            }
        })();
        return () => { alive = false; };
    }, [lookupActiveSession]);

    // Load drop settings on mount. Drives:
    //   - lookaheadDepth: queue depth for /drop/next prefetch.
    //   - requireLocScan: whether the awaiting-location phase fires
    //     after a tote scan. Defaults to true on failure so we don't
    //     accidentally skip a scan the warehouse expects.
    // Starting-location is always required regardless of the setting —
    // the BE anchors the picker's walk_order from it.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Pass module so we get THIS module's require_location_scan
                // (Shelf and Forecast each have their own setting).
                const res = await apiFetch(`/drop/settings?module=${module}`);
                if (!res.ok || !alive) return;
                const d: DropSettings = await res.json();
                if (!alive) return;
                if (typeof d.require_location_scan === "boolean") {
                    setRequireLocScan(d.require_location_scan);
                }
                if (typeof d.lookahead_orders === "number" && d.lookahead_orders >= 1) {
                    setLookaheadDepth(Math.min(20, d.lookahead_orders));
                }
                if (typeof d.show_product_image === "boolean") {
                    setShowProductImage(d.show_product_image);
                }
                if (typeof d.other_skus_count === "number" && d.other_skus_count >= 0) {
                    setOtherSkusCount(Math.min(10, Math.max(0, d.other_skus_count)));
                }
                if (typeof d.location_change_ms === "number" && d.location_change_ms >= 0) {
                    setLocationChangeMs(d.location_change_ms);
                }
                if (typeof d.new_location_highlight_ms === "number" && d.new_location_highlight_ms >= 0) {
                    setNewLocationHighlightMs(d.new_location_highlight_ms);
                }
            } catch { /* fall back to default require-scan = true */ }
        })();
        return () => { alive = false; };
    }, [module]);

    // Fetch a sequence of upcoming locations (current + lookaheads).
    // The BE caps `lookahead` at `settings.picking_lookahead_orders`
    // by default, so one round-trip refills the entire queue.
    const fetchSequence = useCallback(async (
        wo: number | null,
        f: DropFilters,
        excludeLocations: string[] = [],
        depth: number = lookaheadDepth,
        // Barcode the ranking should treat as the picker's position.
        // Defaults to wherever we last put them (currentLocRef);
        // queue-refill calls override with the queue head instead.
        fromLocation: string | null = currentLocRef.current,
        // {sku: qty} already planned FE-side (current target remainder +
        // queued stops) — the BE subtracts these from needs so a refill
        // never re-queues a SKU the existing stops already cover.
        pendingPicks: Record<string, number> | null = null,
    ): Promise<DropLocation[]> => {
        try {
            const params = new URLSearchParams({ module, lookahead: String(depth) });
            if (wo != null) params.set("walkOrder", String(wo));
            if (fromLocation) params.set("currentLocation", fromLocation);
            if (excludeLocations.length > 0) params.set("excludeLocations", excludeLocations.join(","));
            if (pendingPicks && Object.keys(pendingPicks).length > 0) {
                params.set("pendingPicks", JSON.stringify(pendingPicks));
            }
            if (module === "shelf") {
                const wf = dropWireFilters(f, Date.now(), cutoff);
                if (wf.channel !== "all") params.set("channel", wf.channel);
                if (wf.age !== "all")     params.set("age", wf.age);
                if (wf.ageFrom)           params.set("ageFrom", wf.ageFrom);
                if (wf.ageTo)             params.set("ageTo", wf.ageTo);
                if (wf.due)               params.set("due", "1");
            }
            const res = await apiFetch(`/drop/next?${params.toString()}`);
            if (!res.ok) return [];
            const d = await res.json();
            if (d.empty) return [];
            const first: DropLocation = {
                location: d.location,
                locationDisplay: d.locationDisplay,
                walkOrder: d.walkOrder,
                locationHighLow: d.locationHighLow ?? null,
                bayPosition: d.bayPosition ?? null,
                items: d.items ?? [],
            };
            const rest: DropLocation[] = Array.isArray(d.lookaheads)
                ? d.lookaheads.map((l: DropLocation) => ({
                      location: l.location,
                      locationDisplay: l.locationDisplay,
                      walkOrder: l.walkOrder,
                      locationHighLow: l.locationHighLow ?? null,
                      bayPosition: l.bayPosition ?? null,
                      items: l.items ?? [],
                  }))
                : [];
            return [first, ...rest];
        } catch {
            return [];
        }
    }, [module, lookaheadDepth]);

    const setActiveTarget = useCallback((loc: DropLocation) => {
        lastScanItemLocationRef.current = null;
        // The picker is being sent here — by the time the next ranking
        // request fires (refill or pick-complete) this is where they
        // physically are.
        currentLocRef.current = loc.location;
        setTarget(loc);
        const remaining: Record<string, number> = {};
        for (const it of loc.items) remaining[it.sku] = it.qty;
        setRemainingBySku(remaining);
        // When the warehouse has the require_location_scan setting off,
        // skip the per-target-location scan phase entirely — the picker
        // walks to the bay and scans the item directly. The location
        // barcode is still accepted in awaiting-items (idempotent
        // success) so a habitual location scan doesn't fail.
        setPhase(requireLocScan ? "awaiting-location" : "awaiting-items");
    }, [requireLocScan]);

    const advanceToNextLocation = useCallback(async (
        wo: number | null,
        f: DropFilters,
        skipPreloadedQueue = false,
    ) => {
        // Drain the preloaded queue first — saves a round-trip on the
        // awaiting-tote → awaiting-location transition.
        if (!skipPreloadedQueue && lookaheadQueue.length > 0) {
            const [head, ...rest] = lookaheadQueue;
            setActiveTarget(head);
            setLookaheadQueue(rest);
            // Refill in the background to keep the queue at depth. The
            // head just became the target with nothing scanned yet, so
            // its full quantities are still pending alongside the rest.
            const seen = [head.location, ...rest.map(l => l.location)];
            const pending = plannedDropPicks([head, ...rest]);
            fetchSequence(head.walkOrder, f, seen, undefined, head.location, pending).then(seq => {
                // seq[0] is `head` (BE doesn't know we just popped it),
                // so drop entries whose locations we already have.
                const fresh = seq.filter(l => !seen.includes(l.location));
                if (fresh.length > 0) setLookaheadQueue(q => [...q, ...fresh]);
            }).catch(() => {});
            return;
        }
        const seq = await fetchSequence(wo, f);
        if (seq.length === 0) {
            setTarget(null);
            setLookaheadQueue([]);
            setRemainingBySku({});
            // Hold on the current phase (awaiting-tote / awaiting-location /
            // awaiting-items) for the flash duration so the green-tick from
            // the scan that brought us here finishes playing before the page
            // swaps to "ALL CAUGHT UP". Without this delay the picker's
            // success cue is cut off the instant we discover there's no
            // more work — the page reads as having jumped ahead of them.
            window.setTimeout(() => {
                setPhase("complete");
                playToteFinalised();
            }, 900);
            return;
        }
        setActiveTarget(seq[0]);
        setLookaheadQueue(seq.slice(1));
    }, [fetchSequence, setActiveTarget, lookaheadQueue]);

    // ─── mid-pick tote swap ("my tote is full") ───────────────────
    // Locally apply a confirmed swap: point the session at the new tote
    // and re-base the target's item quantities on what's still to pick,
    // so the qty counters read fresh-for-this-tote (the committed units
    // now belong to the old tote's put-away journey).
    const applyToteSwap = useCallback((
        newToteId: string,
        granted?: { granted: number; requested: number }[],
        initialUnits = 0,
    ) => {
        if (granted && granted.some(g => g.granted < g.requested)) {
            showToast("info", t("drop_partial_claim_race"), { toastId: "drop-partial-claim" });
        }
        setToteId(newToteId);
        // A normal replacement tote is fresh and empty. Reopening a
        // closed replacement passes its retained authoritative count.
        setToteUnits(initialUnits);
        playBoxAdd(); flash("success");
        showToast("info", t("picking_tote_swapped", { tote: newToteId }), { toastId: "drop-tote-swap" });
        // The banner item went into the OLD tote — clear it so the
        // green strip never implies the new tote holds it.
        // lastDropLocation deliberately survives: it's the picker's
        // physical trail (for the close-tote card), not tote contents.
        setLastDropped(null);
        setTarget(prev => {
            if (!prev) return prev;
            const remainingItems = prev.items
                .map(i => ({ ...i, qty: remainingBySku[i.sku] ?? i.qty }))
                .filter(i => i.qty > 0);
            return { ...prev, items: remainingItems };
        });
        setRemainingBySku(prev => {
            const next: Record<string, number> = {};
            for (const [sku, qty] of Object.entries(prev)) if (qty > 0) next[sku] = qty;
            return next;
        });
    }, [remainingBySku, flash, t]);

    // POST /drop/tote/swap and handle the outcome. Returns true when
    // the scan was consumed by the swap attempt (success, reopen
    // prompt, or a tote-specific rejection with its own feedback);
    // false when the barcode isn't a swappable tote at all, so the
    // caller falls through to its usual wrong-scan error. Same gesture
    // as Pick Queue: scanning a fresh tote mid-pick swaps to it.
    // With `check` the call only validates the tote: a swappable one
    // opens the confirm-swap prompt instead of switching immediately.
    const performToteSwap = useCallback(async (
        newToteId: string,
        reopen: boolean,
        reopenedUnits = 0,
        check = false,
    ): Promise<boolean> => {
        if (!toteId || !target) return false;
        const res = await apiFetch("/drop/tote/swap", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                module,
                oldToteId: toteId,
                newToteId,
                location: target.location,
                reopen,
                check,
            }),
        });
        const body = await res.json();
        if (body.ok && body.check) {
            playBoxAdd(); flash("success");
            setSwapPrompt({ toteId: body.toteId ?? newToteId });
            return true;
        }
        if (body.ok) {
            applyToteSwap(newToteId, body.granted, reopen ? reopenedUnits : 0);
            return true;
        }
        if (body.reason === "closed_reopenable") {
            // Closed drop tote — same reopen offer the scan-tote phase
            // makes; confirming re-runs the swap with reopen:true.
            setReopenPrompt({
                toteId: body.toteId ?? newToteId,
                units: body.units ?? 0,
                lines: body.lines ?? 0,
                mode: "swap",
            });
            return true;
        }
        if (body.reason === "in_session_takeover") {
            playError(); flash("error");
            showToast("error", t("drop_tote_in_use_elsewhere"), { toastId: "drop-swap-in-session-takeover" });
            return true;
        }
        if (body.reason === "tote_in_use_elsewhere" || body.reason === "tote_unavailable") {
            playError(); flash("error");
            showToast(
                "error",
                t(body.reason === "tote_in_use_elsewhere" ? "drop_tote_in_use_elsewhere" : "drop_tote_unavailable"),
                { toastId: `drop-swap-${body.reason}` },
            );
            return true;
        }
        // not_a_tote / same_tote → not a swap; let the caller error.
        return false;
    }, [toteId, target, module, applyToteSwap, flash, t]);

    // Confirm reopen of the closed tote in `reopenPrompt`.
    //   mode "start": flips it back to 'open' on the BE (keeping its
    //     picked contents) then advances into picking exactly like a
    //     normal tote scan would.
    //   mode "swap": mid-pick — /drop/tote/swap with reopen:true, which
    //     commits the current tote's partial picks and continues at the
    //     same target with the reopened tote.
    const handleReopen = useCallback(async () => {
        const prompt = reopenPrompt;
        if (!prompt || reopeningRef.current) return;
        reopeningRef.current = true;
        setReopening(true);
        try {
            if (prompt.mode === "swap") {
                const consumed = await performToteSwap(prompt.toteId, true, prompt.units);
                if (!consumed) {
                    playError(); flash("error");
                    showToast("error", t("drop_tote_unavailable"), { toastId: "drop-reopen-failed" });
                }
                setReopenPrompt(null);
                return;
            }
            const res = await apiFetch("/drop/tote/reopen", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module, toteId: prompt.toteId }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.ok) {
                playError(); flash("error");
                showToast("error", t("drop_tote_unavailable"), { toastId: "drop-reopen-failed" });
                setReopenPrompt(null);
                return;
            }
            playBoxAdd(); flash("success");
            setToteId(prompt.toteId);
            setToteUnits(prompt.units);
            setReopenPrompt(null);
            await advanceToNextLocation(walkOrder, filters);
        } catch {
            playError(); flash("error");
            showToast("error", t("drop_tote_unavailable"), { toastId: "drop-reopen-failed" });
            setReopenPrompt(null);
        } finally {
            reopeningRef.current = false;
            setReopening(false);
        }
    }, [reopenPrompt, module, walkOrder, filters, advanceToNextLocation, performToteSwap, flash, t]);

    // Confirm the swap in `swapPrompt` (rescan of the same tote or the
    // Confirm tap): runs the real /drop/tote/swap. The tote was
    // validated a moment ago, but the state can have moved (another
    // picker took it) — the real call re-validates and any rejection
    // surfaces through performToteSwap's normal feedback.
    const handleSwapConfirm = useCallback(async () => {
        const prompt = swapPrompt;
        if (!prompt || swappingRef.current) return;
        swappingRef.current = true;
        setSwapping(true);
        try {
            const consumed = await performToteSwap(prompt.toteId, false);
            if (!consumed) {
                playError(); flash("error");
                showToast("error", t("drop_tote_unavailable"), { toastId: "drop-swap-failed" });
            }
        } catch {
            playError(); flash("error");
            showToast("error", t("drop_tote_unavailable"), { toastId: "drop-swap-failed" });
        } finally {
            setSwapPrompt(null);
            swappingRef.current = false;
            setSwapping(false);
        }
    }, [swapPrompt, performToteSwap, flash, t]);

    // Confirm the second current-tote scan (or Confirm tap). The server
    // validates the active session still owns this exact tote, releases
    // unused reservations, and decides whether it belongs in put-away or
    // can return straight to the available pool.
    const handleCloseTote = useCallback(async () => {
        const prompt = closePromptRef.current;
        if (!prompt || closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            const res = await apiFetch("/drop/tote/close", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module, toteId: prompt.toteId }),
            });
            const body = await res.json().catch(() => ({}));
            // no_active_session means the BE already ended this session —
            // pick-complete does that the moment the module runs out of
            // work, while the tote is still in the picker's hands and this
            // screen still shows it open (2026-09-28 tote 5147). Current
            // BEs settle the tote and answer ok anyway; an older BE (the
            // hand-updated production box can lag Render) still says
            // no_active_session. Either way the tote holds whatever
            // pick-complete already stamped on it: any picked stock has
            // flipped it 'closed' into the put-away queue, and an empty one
            // gets recycled by the stale-tote sweep. Retrying can never
            // change that, so treat it as closed rather than a server error.
            const endedElsewhere = !body.ok && body.reason === "no_active_session";
            if ((!res.ok || !body.ok) && !endedElsewhere) {
                playError(); flash("error");
                showToast(
                    "error",
                    body.reason === "tote_mismatch"
                        ? t("drop_tote_unavailable")
                        : body.reason
                            ? t("drop_close_failed", { reason: String(body.reason) })
                            : t("server_error"),
                    { toastId: "drop-close-failed" },
                );
                // Nothing was closed — if we came from the resume modal, put it
                // back so the operator still has a way into their session.
                dismissClosePrompt();
                return;
            }

            const putAway = endedElsewhere ? prompt.units > 0 : Boolean(body.putAway);
            playToteFinalised(); flash("success");
            showToast(
                "info",
                t(putAway ? "drop_tote_closed_toast" : "drop_tote_closed_empty_toast", {
                    tote: prompt.toteId,
                }),
                { toastId: "drop-tote-closed" },
            );
            if (putAway) setPutAwayCount(count => count + 1);

            // The close endpoint completed the durable session. Clear every
            // local pointer into it so the next scan starts at the module's
            // normal first screen rather than reviving the old target. The
            // resume stash goes too — the session it pointed at is now closed,
            // so the modal must NOT come back.
            resumeAwaitingCloseRef.current = null;
            updateClosePrompt(null);
            setToteId(null);
            setTarget(null);
            setLookaheadQueue([]);
            setRemainingBySku({});
            setLastDropped(null);
            // Carry the trail onto the post-close "scan current location"
            // screen before the session-scoped copy is wiped below.
            setPostCloseLastLocation(prompt.lastLocation ?? null);
            setLastDropLocation(null);
            setWalkOrder(null);
            setToteUnits(0);
            lastScanItemLocationRef.current = null;
            setPhase("awaiting-starting-location");
        } catch (err) {
            console.error("drop close tote error", err);
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-close-error" });
            // A failed close changes nothing server-side from the FE's point
            // of view. Dismiss only the prompt and leave the pick session live
            // (or restore the resume modal, if that is where we came from).
            dismissClosePrompt();
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [module, flash, t, updateClosePrompt, dismissClosePrompt]);

    const handleResume = useCallback(async () => {
        const session = resumePrompt;
        if (!session || resumingRef.current) return;
        resumingRef.current = true;
        setResuming(true);
        try {
            const sessionFilters = session.filters ?? { channel: "all", age: "all" };
            const action = deriveDropResumeAction(session, requireLocScan);
            setWalkOrder(session.walkOrder);
            setToteId(session.toteId);
            setToteUnits(getDropSessionUnitCount(session));
            // Seed the local last-item-location trail from the server
            // session so a close-tote right after resuming still shows
            // where they were.
            setLastDropLocation(deriveSessionLastLocation(session));
            setPostCloseLastLocation(null);
            setFilters(sessionFilters);
            setLookaheadQueue([]);
            setResumePrompt(null);

            if (action === "awaiting-tote") {
                setTarget(null);
                setRemainingBySku({});
                lastScanItemLocationRef.current = null;
                setPhase("awaiting-tote");
            } else if (action === "advance") {
                setTarget(null);
                setRemainingBySku({});
                lastScanItemLocationRef.current = null;
                await advanceToNextLocation(session.walkOrder, sessionFilters, true);
            } else if (session.target) {
                setActiveTarget(session.target);
                // A target returned by /session/active is already persisted
                // on the server, so a resumed scan must not send it again.
                lastScanItemLocationRef.current = session.target.location;
            }

            showToast(
                "info",
                session.toteId
                    ? t("drop_resumed_toast", { tote: session.toteId })
                    : t("drop_resumed_no_tote_toast"),
                { toastId: "drop-resumed" },
            );
        } finally {
            resumingRef.current = false;
            setResuming(false);
        }
    }, [resumePrompt, requireLocScan, advanceToNextLocation, setActiveTarget, t]);

    // Resume modal → "Close tote". The operator is holding a part-full tote and
    // does not want to carry on picking into it, so we END the session and send
    // the tote to put-away rather than offering a "start over" that reads as if
    // their picks are discarded. Hands off to the SAME rescan-or-confirm prompt
    // the mid-pick close uses (handleCloseTote / closePrompt) so there is one
    // close path, one confirmation, and one set of strings.
    //
    // resumePrompt is cleared BOTH via state and directly on the ref: the ref
    // is otherwise synced by an effect, and submitScan's resume interception
    // (which returns early) would swallow the confirming rescan before it
    // reached the close-prompt guard underneath.
    const handleCloseToteFromResume = useCallback(() => {
        const session = resumePrompt;
        if (!session?.toteId || resumingRef.current) return;
        resumeAwaitingCloseRef.current = session;
        resumePromptRef.current = null;
        setResumePrompt(null);
        updateClosePrompt({
            toteId: session.toteId,
            units: getDropSessionUnitCount(session),
            // The local trail is empty on this path (fresh page load) —
            // the server session's current_location is the last accepted
            // item scan, which is exactly the same fact.
            lastLocation: deriveSessionLastLocation(session),
        });
    }, [resumePrompt, updateClosePrompt]);

    // No tote on the session yet (nothing physical to lose — only unpicked
    // reservations, which the BE cancels). Abandoning is the honest action here;
    // /drop/tote/close would reject with tote_mismatch.
    const handleDiscardSession = useCallback(async () => {
        if (!resumePrompt || resumingRef.current) return;
        resumingRef.current = true;
        setResuming(true);
        try {
            const res = await apiFetch("/drop/session/abandon", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.ok) throw new Error("drop session abandon failed");

            setResumePrompt(null);
            setWalkOrder(null);
            setToteId(null);
            setToteUnits(0);
            setTarget(null);
            setRemainingBySku({});
            setLookaheadQueue([]);
            setFilters({ channel: "all", age: "all" });
            setLastDropped(null);
            setLastDropLocation(null);
            setPostCloseLastLocation(null);
            lastScanItemLocationRef.current = null;
            setPhase("awaiting-starting-location");
        } catch (err) {
            console.error("drop session abandon error", err);
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-abandon-error" });
        } finally {
            resumingRef.current = false;
            setResuming(false);
        }
    }, [resumePrompt, module, flash, t]);

    const handleTakeover = useCallback(async () => {
        const prompt = takeoverPrompt;
        if (!prompt || takingOverRef.current) return;
        takingOverRef.current = true;
        setTakingOver(true);
        try {
            const res = await apiFetch("/drop/tote/takeover", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module, toteId: prompt.toteId }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.ok) throw new Error("drop tote takeover failed");
            setTakeoverPrompt(null);
            playSuccess();
            showToast("info", t("drop_takeover_done", { tote: prompt.toteId }), { toastId: "drop-takeover-done" });
        } catch (err) {
            console.error("drop tote takeover error", err);
            playError(); flash("error");
            showToast("error", t("drop_tote_unavailable"), { toastId: "drop-takeover-error" });
        } finally {
            takingOverRef.current = false;
            setTakingOver(false);
        }
    }, [takeoverPrompt, module, flash, t]);

    // Pre-warm the lookahead queue so it's full by the time the
    // picker scans their tote. Re-runs whenever picker walk_order,
    // filters, or settings change.
    useEffect(() => {
        if (phase !== "awaiting-tote" && phase !== "awaiting-starting-location") return;
        if (lookaheadQueue.length >= lookaheadDepth) return;
        let alive = true;
        const seen = lookaheadQueue.map(l => l.location);
        // Queued stops (and, on a mid-session resume, the un-scanned
        // remainder at a live target) are planned picks with no claims
        // yet — tell the BE so the top-up doesn't re-queue their SKUs.
        const pending = plannedDropPicks(lookaheadQueue, target ? remainingBySku : undefined);
        fetchSequence(walkOrder, filters, seen, undefined, undefined, pending).then(seq => {
            if (!alive) return;
            const fresh = seq.filter(l => !seen.includes(l.location));
            if (fresh.length > 0) {
                setLookaheadQueue(q => [...q, ...fresh]);
            } else if (lookaheadQueue.length === 0) {
                // No work anywhere AND nothing queued. Skip the
                // location/tote scans — there's nothing for the picker
                // to drop. Jump straight to the complete state so they
                // see "ALL CAUGHT UP" instead of being asked to scan
                // a starting location followed by a tote followed by
                // … nothing.
                setPhase("complete");
            }
            setInitialized(true);
        }).catch(() => { if (alive) setInitialized(true); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, walkOrder, filters, lookaheadDepth]);

    // ─── accepted-unit application ───────────────────────────────
    // Shared tail of an accepted item pick: decrement the remaining
    // counter, drive the green banner / progress / pick-speed, commit it,
    // and run pick-complete when the location is finished
    // flow with its optimistic hero swap. Item scans apply 1 unit;
    // the manual-pick modal ("barcode not scanning") applies the
    // confirmed quantity in one call. Callers validate the unit count
    // against remainingBySku BEFORE calling and hold the inFlight
    // guard around the await.
    const applyAcceptedUnits = useCallback(async (item: DropItem, units: number) => {
        if (!target || !toteId) return;
        const previousRemaining = remainingBySku;
        const previousLastDropped = lastDropped;
        const previousLastDropLocation = lastDropLocation;
        const left = Math.max(0, (remainingBySku[item.sku] ?? 0) - units);
        const optimisticRemaining = { ...remainingBySku, [item.sku]: left };
        setRemainingBySku(optimisticRemaining);
        // Record where the unit came from, for the close-tote card.
        // Optimistic like lastDropped; rolled back on the same paths.
        setLastDropLocation(target.locationDisplay ?? target.location);
        // Record the dropped unit for the green banner — `item`
        // is whichever line at this location the scan matched,
        // not necessarily the hero SKU.
        setLastDropped({
            sku: item.sku,
            title: item.productName ?? null,
            barcode: item.productBarcode ?? null,
            imageUrl: item.productImageUrl ?? null,
            thumbnailUrl: item.productThumbnailUrl ?? null,
        });
        // Optimistic bump so the shared progress bar / counter
        // moves in time with the scan, not on the next poll.
        // The authoritative refresh after pick-complete (and
        // the 5s tick) reconciles if other pickers contributed.
        setProgress(p => ({ ...p, picked: Math.min(p.total, p.picked + units) }));
        for (let i = 0; i < units; i++) recordPick();
        // Once per scan, not once per unit — a multi-unit drop is one motion.
        cycleRef.current.complete();

        // The picker's pacing cue stays optimistic: valid scans beep before
        // the commit round-trip unless this is the final item with no queued
        // location. That path deliberately reserves feedback for the amber
        // complete tick below.
        const optimisticTotalLeft = Object.values(optimisticRemaining).reduce((s, n) => s + n, 0);
        if (shouldPlayOptimisticDropFeedback(optimisticTotalLeft, lookaheadQueue.length)) {
            playBoxAdd(); flash("success");
        }

        // Persist before any location-complete transition. This await stays
        // inside submitScan/manual pick's existing in-flight guard, so scans
        // remain strictly serial without a second queue.
        const previousSentLocation = lastScanItemLocationRef.current;
        const firstScanAtThisLocation = previousSentLocation !== target.location;
        if (firstScanAtThisLocation) lastScanItemLocationRef.current = target.location;

        let grantedUnits = units;
        try {
            const res = await apiFetch("/drop/scan-item", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    module,
                    toteId,
                    location: target.location,
                    sku: item.sku,
                    qty: units,
                    target: firstScanAtThisLocation ? target : undefined,
                }),
            });
            const body = await res.json();
            if ((!res.ok || body.ok === false)
                && (body.reason === "no_active_session" || body.reason === "tote_mismatch")) {
                // The session was ended or superseded elsewhere while this
                // scan was in flight. Undo the optimistic unit exactly as a
                // network failure would, then discard all stale session state
                // so the next scan starts a fresh Drop flow.
                setRemainingBySku(previousRemaining);
                setLastDropped(previousLastDropped);
                setLastDropLocation(previousLastDropLocation);
                lastScanItemLocationRef.current = previousSentLocation;
                refreshProgress();
                playError(); flash("error");
                showToast("error", t("drop_session_lost"), { toastId: "drop-session-lost" });
                setToteId(null);
                setToteUnits(0);
                setTarget(null);
                setRemainingBySku({});
                setLookaheadQueue([]);
                setWalkOrder(null);
                setPhase("awaiting-starting-location");
                return;
            }
            if (!res.ok && body.reason !== "no_units_available") {
                throw new Error(`drop scan-item failed (${res.status})`);
            }
            const grant = Array.isArray(body.granted)
                ? body.granted.find((entry: { sku?: string }) => entry.sku === item.sku)
                : null;
            grantedUnits = Math.min(
                units,
                Math.max(0, Number(grant?.granted ?? (typeof body.granted === "number" ? body.granted : 0))),
            );

            if (grantedUnits < units) {
                const reconciledRemaining = {
                    ...previousRemaining,
                    [item.sku]: remainingAfterGrantedUnits(previousRemaining[item.sku] ?? 0, units, grantedUnits),
                };
                setRemainingBySku(reconciledRemaining);
                // Shared progress is forward-only. Refreshing makes this
                // optimistic over-count stall until authoritative picks
                // catch up, matching pick-complete rollback below.
                refreshProgress();
                if (grantedUnits === 0) {
                    setLastDropped(previousLastDropped);
                    setLastDropLocation(previousLastDropLocation);
                    playError(); flash("error");
                    showToast("error", t("drop_partial_claim_race"), { toastId: "drop-partial-claim" });
                    return;
                }
                showToast("info", t("drop_partial_claim_race"), { toastId: "drop-partial-claim" });
            }
        } catch (err) {
            console.error("drop scan-item error", err);
            setRemainingBySku(previousRemaining);
            setLastDropped(previousLastDropped);
            setLastDropLocation(previousLastDropLocation);
            lastScanItemLocationRef.current = previousSentLocation;
            // The displayed shared progress can only stall, never rewind.
            refreshProgress();
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-scan-item-error" });
            return;
        }

        const updated = {
            ...previousRemaining,
            [item.sku]: remainingAfterGrantedUnits(previousRemaining[item.sku] ?? 0, units, grantedUnits),
        };
        // Count only units the authoritative scan endpoint accepted. A race
        // shortfall therefore shows the physical tote total, not the quantity
        // the picker originally requested through a scan/manual confirmation.
        setToteUnits(count => count + grantedUnits);
        const totalLeft = Object.values(updated).reduce((s, n) => s + n, 0);
        if (totalLeft === 0) {
            // All items at this location are picked. Snapshot
            // the pre-swap state so we can roll back if the BE
            // rejects the pick-complete (rare).
            const swapSnapshot = { target, queue: lookaheadQueue, walkOrder, remainingBySku: updated, lastDropped };
            const justFinishedLocation = target.location;
            // Track whether we already drove the page into the
            // "complete" state optimistically. If we did, the
            // [phase] effect has already scheduled the tick →
            // "ALL CAUGHT UP" swap, and the post-await ELSE
            // branch must NOT redo the tick state — re-setting
            // completeStage="tick" / scanStatus="complete" once
            // that swap has fired flips the UI back to the
            // amber tick, and the setPhase("complete") below
            // is a no-op (phase didn't change) so the effect
            // doesn't re-run to schedule another swap.
            let optimisticallyCompleted = false;
            // Optimistic hero swap — drain the queue immediately
            // so the picker sees the next destination without
            // network latency. If the queue is empty we KNOW
            // (from the last BE response) there's no more work,
            // so flip to "complete" right away — that plays the
            // amber tick instantly instead of leaving an icon
            // visible while pick-complete is in flight.
            if (lookaheadQueue.length > 0) {
                const [head, ...rest] = lookaheadQueue;
                setActiveTarget(head);
                setLookaheadQueue(rest);
                setWalkOrder(target.walkOrder);
            } else {
                setTarget(null);
                setRemainingBySku({});
                setWalkOrder(target.walkOrder);
                // Picker just finished the last item — fire the
                // amber complete-tick. Setting completeStage +
                // scanStatus inline (batched with setPhase) makes
                // the first paint show the tick directly; without
                // it React paints one frame of completeStage=
                // "message" before the effect runs, flashing
                // "ALL CAUGHT UP" between the hero and the tick.
                playTickOnNextCompleteRef.current = true;
                setCompleteStage("tick");
                setScanStatus("complete");
                setPhase("complete");
                optimisticallyCompleted = true;
                // No "success" flash here — phase=complete fires
                // the completeStage effect which plays the amber
                // tick. A green tick on top of that adds a
                // transient state the picker has to parse.
            }
            const res = await apiFetch("/drop/pick-complete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    module, toteId, location: target.location,
                    walkOrder: target.walkOrder,
                    // Wire shape: custom-range bounds composed into ISO
                    // timestamps (the raw date/time parts stay FE-only).
                    filters: dropWireFilters(filters, Date.now(), cutoff),
                    lookahead: lookaheadDepth,
                    excludeLocations: [justFinishedLocation],
                }),
            });
            const body = await res.json();
            if (!body.ok) {
                // Roll back the optimistic swap so the picker
                // is sent back to the original location.
                // lastDropped restore is best-effort: the whole
                // location's picks are void, but the snapshot
                // only reaches back one scan (rare 409/500 path).
                // Arm transition suppression BEFORE the rollback: this puts the
                // hero back on the location the picker is still standing at, and
                // a "NEW LOCATION" takeover plus success cue there would
                // celebrate a move that never happened — over the top of this
                // failure's own error flash.
                setSuppressTransition(n => n + 1);
                setTarget(swapSnapshot.target);
                setLookaheadQueue(swapSnapshot.queue);
                setWalkOrder(swapSnapshot.walkOrder);
                setRemainingBySku(swapSnapshot.remainingBySku);
                setLastDropped(swapSnapshot.lastDropped);
                setPhase("awaiting-items");
                playError(); flash("error");
                showToast("error", t("drop_pick_complete_failed"), { toastId: "drop-pc-fail" });
                // Reconcile against the BE now the optimistic
                // bumps for this location are void. mergeProgress
                // is forward-only, so the over-count just stalls
                // the bar until real picks catch up — it is NEVER
                // walked backwards (the picker already saw it
                // advance; rewinding it here is the bug we're
                // avoiding). Rare path: pick-complete only fails
                // on a 409/500.
                refreshProgress();
                return;
            }
            // Pull the shared BE state now this location's picks
            // are committed (forward-only via mergeProgress).
            refreshProgress();
            // BE confirmed. No success sound here: the optimistic hero swap
            // above already changed PickingScreen's `location`, and its
            // location-change effect plays playSuccess() as it mounts the
            // takeover. Playing it again on confirm double-fired the cue one
            // round-trip later. (Before the satisfied-hero pin, PickingScreen
            // unmounted across the swap and that effect never ran, so this
            // call was the ONLY success sound — hence its removal now.)
            // Module fully caught up still gets playToteFinalised below.
            // Authoritative refresh from the response. If the
            // BE's next matches the optimistic head we already
            // swapped to, setActiveTarget would reset the
            // remaining counter — so only update on mismatch.
            if (body.next) {
                const next: DropLocation = {
                    location: body.next.location,
                    locationDisplay: body.next.locationDisplay,
                    walkOrder: body.next.walkOrder,
                    items: body.next.items ?? [],
                };
                const optimisticHead = lookaheadQueue[0]?.location;
                if (optimisticHead !== next.location) {
                    setActiveTarget(next);
                }
                const fresh: DropLocation[] = Array.isArray(body.lookaheads)
                    ? body.lookaheads.map((l: DropLocation) => ({
                          location: l.location,
                          locationDisplay: l.locationDisplay,
                          walkOrder: l.walkOrder,
                          items: l.items ?? [],
                      }))
                    : [];
                setLookaheadQueue(fresh);
            } else {
                // No more work — module is caught up. If we
                // already drove the page into "complete"
                // optimistically, the [phase] effect has
                // already handled the tick → "ALL CAUGHT UP"
                // swap; touching completeStage/scanStatus here
                // would flip the UI back to the tick with no
                // effect re-run to schedule another swap. Only
                // drive the transition when the optimistic
                // path swapped to a queue head and the BE has
                // now contradicted that.
                if (!optimisticallyCompleted) {
                    playTickOnNextCompleteRef.current = true;
                    setTarget(null);
                    setLookaheadQueue([]);
                    setRemainingBySku({});
                    setCompleteStage("tick");
                    setScanStatus("complete");
                    setPhase("complete");
                }
                playToteFinalised();
            }
        }
    }, [target, remainingBySku, lookaheadQueue, walkOrder, lastDropped, lastDropLocation,
        toteId, filters, lookaheadDepth, module, t, flash, setActiveTarget, recordPick,
        refreshProgress]);

    // ─── scan submission ─────────────────────────────────────────
    const submitScan = useCallback(async (raw?: string) => {
        const candidate = raw ?? scanValue;
        const barcode = candidate.trim();
        if (!barcode) return;
        // Resume (or discard) in flight: the modal is already gone but the
        // resumed target has not landed, so `phase` is still the mount
        // default "awaiting-starting-location". Letting a scan through
        // here would read a bay barcode as a brand-new session start and
        // supersede the session the picker just chose to resume
        // (2026-10-13 tote 3818). The hero shows a loader for this window;
        // drop the scan so it cannot act on a phase that is about to
        // change under it. Silently: the common arrival here is the
        // scanner double-firing the tote rescan that confirmed the resume,
        // which must not answer a successful resume with an error tone.
        if (resumingRef.current) return;
        // Resume and takeover prompts both support hands-free confirmation:
        // rescan the tote named in the modal. Any other barcode is rejected
        // without leaking through to the phase handler underneath.
        if (resumePromptRef.current) {
            if (resumePromptRef.current.toteId && barcode === resumePromptRef.current.toteId) {
                await handleResume();
            } else {
                playError(); flash("error");
            }
            return;
        }
        if (takeoverPromptRef.current) {
            if (barcode === takeoverPromptRef.current.toteId) {
                await handleTakeover();
            } else {
                playError(); flash("error");
            }
            return;
        }
        // Reopen prompt open: a scan of the SAME tote confirms reopen,
        // anything else is rejected. Mirrors book-in's modal scan
        // interception so the picker can confirm by rescanning rather
        // than reaching for the on-screen button.
        if (reopenPromptRef.current) {
            if (barcode === reopenPromptRef.current.toteId) {
                await handleReopen();
            } else {
                playError(); flash("error");
            }
            return;
        }
        // Confirm-swap prompt open: only rescanning the SAME new tote
        // confirms the swap; anything else is rejected (Cancel is on
        // screen). Same shape as the reopen interception above.
        if (swapPromptRef.current) {
            if (barcode === swapPromptRef.current.toteId) {
                await handleSwapConfirm();
            } else {
                playError(); flash("error");
            }
            return;
        }
        // Close prompt open: only rescanning the SAME current tote confirms.
        // This interception deliberately precedes hardware dedup so the quick
        // safety rescan cannot be swallowed as a scanner double-fire.
        if (closePromptRef.current) {
            if (barcode === closePromptRef.current.toteId) {
                await handleCloseTote();
            } else {
                playError(); flash("error");
            }
            return;
        }
        // Move prompt open: every scan is a candidate destination location.
        // Bypass hardware dedup so a deliberate rescan can retry immediately.
        const movePromptOpen = !!movePromptRef.current;
        // Drop scanner-double-fires: same barcode within DEDUP_WINDOW_MS
        // of the previous one. The first is already in flight (or about
        // to be) and will advance the target on success; processing the
        // duplicate against the new target almost always fails with
        // `drop_wrong_location`.
        if (!movePromptOpen) {
            const now = performance.now();
            if (lastScanRef.current
                && lastScanRef.current.barcode === barcode
                && (now - lastScanRef.current.at) < DEDUP_WINDOW_MS) {
                return;
            }
            lastScanRef.current = { barcode, at: now };
        }
        // Already processing the previous scan — stash this one and let
        // the in-flight call's `finally` drain it. See the ref-declaration
        // comment above for why.
        if (inFlightRef.current) {
            scanQueueRef.current.push(barcode);
            return;
        }
        inFlightRef.current = true;
        setSubmitting(true);
        try {
            if (movePromptOpen) {
                setMoving(true);
                try {
                    const res = await apiFetch("/drop/session/move", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ module, location: barcode }),
                    });
                    const body = await res.json();
                    if (!body.ok) {
                        playError(); flash("error");
                        const msg = body.reason === "wrong_warehouse"
                            ? t("scan_wrong_warehouse", { warehouse: body.locationWarehouse ?? "?" })
                            : t("drop_move_invalid_location");
                        showToast("error", msg, { toastId: "drop-move-invalid-location" });
                        return;
                    }
                    updateMovePrompt(null);
                    // Stamp the dedup ref NOW (dedup was bypassed while the
                    // prompt was open): a scanner double-fire of the
                    // destination queued during this request drains right
                    // after the prompt closes, and without this stamp it
                    // would replay into the normal phase handler as a
                    // spurious wrong-location/duplicate action.
                    lastScanRef.current = { barcode, at: performance.now() };
                    playBoxAdd(); flash("success");
                    currentLocRef.current = barcode;
                    setWalkOrder(body.walkOrder);
                    setLookaheadQueue([]);
                    setRemainingBySku({});
                    await advanceToNextLocation(body.walkOrder, filters, true);
                } finally {
                    setMoving(false);
                }
                return;
            }
            // Missing-item flow scans — dispatched off the refs, not the
            // phase state, so a scan racing the flag-click render can't
            // fall through to the normal phase handlers (see the ref
            // declarations for why).
            const missingFlow = missingRef.current;
            if (missingFlow && missingStageRef.current === "gate") {
                // The gate proves the picker is at the flagged shelf before
                // a single item is counted — the location barcode only.
                if (barcode !== missingFlow.location) {
                    playError(); flash("error");
                    showToast("error", t("drop_wrong_location"), { toastId: "drop-missing-wrong-loc" });
                    return;
                }
                playBoxAdd(); flash("success");
                missingStageRef.current = "counting";
                setPhase("missing-counting");
                return;
            }
            if (missingFlow && missingStageRef.current === "counting") {
                // Habit rescan of the location label — idempotent no-op,
                // same courtesy as awaiting-items.
                if (barcode === missingFlow.location) {
                    playBoxAdd(); flash("success");
                    return;
                }
                const res = await apiFetch("/drop/missing/scan-item", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ module, checkId: missingFlow.checkId, barcode }),
                });
                const body = await res.json();
                if (!body?.ok) {
                    playError(); flash("error");
                    showToast("error", t("server_error"), { toastId: "drop-missing-scan" });
                    return;
                }
                if (body.stock_check_outcome === "unknown") {
                    playError(); flash("error");
                    showToast("error", t("picking_missing_unknown_barcode"), { toastId: "drop-missing-unknown" });
                    return;
                }
                // Surplus: a unit beyond what the count can book on its own —
                // a SKU this location doesn't stock (known_not_here), an
                // expected SKU past its expected count (surplus), or another
                // unit of a SKU added earlier this session (already_added).
                // The classifier records none of these, so without the prompt
                // the scan would beep success and silently drop the unit
                // (W25L7, 2026-08-21: multi-unit finds booked as 1). The
                // picker is asked, and only an explicit yes routes through
                // /stock-check/add.
                if (body.sku && (body.stock_check_outcome === "known_not_here"
                    || body.stock_check_outcome === "surplus"
                    || body.stock_check_outcome === "already_added")) {
                    setSurplusPrompt({
                        sku: body.sku, barcode,
                        extraUnit: body.stock_check_outcome !== "known_not_here",
                    });
                    return;
                }
                playBoxAdd(); flash("success");
                if (Array.isArray(body.stock_check_lines)) {
                    const lines: StockCheckLine[] = body.stock_check_lines;
                    missingRef.current = { ...missingFlow, lines };
                    setMissing(prev => prev ? { ...prev, lines } : prev);
                }
                return;
            }
            if (phase === "awaiting-starting-location") {
                const res = await apiFetch("/drop/session/start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ module, startingLocation: barcode, filters }),
                });
                const body = await res.json();
                if (!body.ok) {
                    playError(); flash("error");
                    // active_tote_session: the server still has a live
                    // session holding a tote for this picker (this screen
                    // lost track of it — stale tab, lost reset, a race the
                    // BE refused to resolve by discarding their picks). Put
                    // the resume choice back in front of them instead of
                    // reporting the bay as invalid.
                    if (body.reason === "active_tote_session") {
                        showToast(
                            "error",
                            t("drop_start_blocked_by_tote", { tote: String(body.toteId ?? "") }),
                            { toastId: "drop-start-blocked" },
                        );
                        const session = await lookupActiveSession();
                        if (session) {
                            setLookaheadQueue([]);
                            resumePromptRef.current = session;
                            setResumePrompt(session);
                        }
                        return;
                    }
                    // wrong_warehouse: the barcode IS a live location,
                    // just not in the operator's session warehouse —
                    // name it so they know to get their warehouse
                    // changed instead of re-scanning forever.
                    const msg = body.reason === "wrong_warehouse"
                        ? t("scan_wrong_warehouse", { warehouse: body.locationWarehouse ?? "?" })
                        : t("drop_invalid_starting_location");
                    showToast("error", msg, { toastId: "drop-bad-start-loc" });
                    return;
                }
                playBoxAdd(); flash("success");
                // A fresh anchor supersedes the previous session's trail.
                setPostCloseLastLocation(null);
                // The prewarm while awaiting this scan was unanchored.
                // Discard it so the existing effect refills once from the
                // picker position returned by /drop/session/start.
                setLookaheadQueue([]);
                currentLocRef.current = barcode;
                setWalkOrder(body.walkOrder);
                setPhase("awaiting-tote");
                return;
            }
            if (phase === "awaiting-tote") {
                const res = await apiFetch("/drop/tote/start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ module, toteId: barcode }),
                });
                const body = await res.json();
                if (!body.ok) {
                    // Closed tote → offer to reopen rather than error.
                    // The modal handles confirm/cancel from here.
                    if (body.reason === "closed_reopenable") {
                        setReopenPrompt({
                            toteId: body.toteId ?? barcode,
                            units: body.units ?? 0,
                            lines: body.lines ?? 0,
                            mode: "start",
                        });
                        return;
                    }
                    if (body.reason === "in_session_takeover") {
                        setTakeoverPrompt({
                            toteId: body.toteId ?? barcode,
                            units: body.units ?? 0,
                            lines: body.lines ?? 0,
                            pickerName: body.pickerName ?? t("drop_takeover_unknown_picker"),
                        });
                        return;
                    }
                    playError(); flash("error");
                    const key =
                        body.reason === "not_a_tote"            ? "drop_not_a_tote"             :
                        body.reason === "tote_in_use_elsewhere" ? "drop_tote_in_use_elsewhere"  :
                        body.reason === "tote_unavailable"      ? "drop_tote_unavailable"       :
                        "drop_tote_unavailable";
                    showToast("error", t(key), { toastId: `drop-${body.reason}` });
                    return;
                }
                playBoxAdd(); flash("success");
                setToteId(barcode);
                setToteUnits(0);
                // Fetch first target location.
                await advanceToNextLocation(walkOrder, filters);
                return;
            }
            if (phase === "awaiting-location") {
                if (!target) return;
                if (barcode !== target.location) {
                    // The first current-tote scan is only an invitation to
                    // close; a second matching scan/Confirm is the safety
                    // action that actually ends the session.
                    if (barcode === toteId) {
                        playBoxAdd(); flash("success");
                        updateClosePrompt({ toteId, units: toteUnits, lastLocation: lastDropLocation });
                        return;
                    }
                    // Tote full? Scanning a fresh tote here offers to swap
                    // to it (confirm prompt; same gesture as Pick Queue).
                    // Falls through to the wrong-location error when the
                    // barcode isn't a tote.
                    if (await performToteSwap(barcode, false, 0, true)) return;
                    playError(); flash("error");
                    showToast("error", t("drop_wrong_location"), { toastId: "drop-wrong-loc" });
                    return;
                }
                playBoxAdd(); flash("success");
                setPhase("awaiting-items");
                return;
            }
            if (phase === "awaiting-items") {
                if (!target) return;
                // The target's location barcode is accepted as an
                // idempotent confirm. Two paths land here:
                //   - requireLocScan=true and the picker already scanned
                //     the location in awaiting-location, then rescans
                //     it (e.g. trigger held down). Friendlier than
                //     surfacing wrong_item.
                //   - requireLocScan=false: there's no awaiting-location
                //     phase, but a picker scanning the bay barcode out
                //     of habit shouldn't get an error.
                if (barcode === target.location) {
                    playBoxAdd(); flash("success");
                    return;
                }
                // Match the scanned barcode to an item at this location
                // by SKU, primary product barcode, or any alternate barcode
                // included in the drop payload.
                const item = target.items.find(i =>
                    barcode === i.sku ||
                    barcode === i.productBarcode ||
                    (i.barcodes ?? []).includes(barcode)
                );
                if (!item) {
                    // Current tote means "offer to close". Opening the
                    // prompt is harmless; only its same-tote rescan or
                    // explicit Confirm can complete the session.
                    if (barcode === toteId) {
                        playBoxAdd(); flash("success");
                        updateClosePrompt({ toteId, units: toteUnits, lastLocation: lastDropLocation });
                        return;
                    }
                    // Tote full mid-location? Scanning a fresh tote
                    // swaps to it: the units already scanned commit to
                    // the old tote (→ put-away queue) and picking
                    // continues here with the new one — after the picker
                    // confirms the prompt. Falls through to the wrong-item
                    // error when it isn't a tote.
                    if (await performToteSwap(barcode, false, 0, true)) return;
                    playError(); flash("error");
                    showToast("error", t("drop_wrong_item"), { toastId: "drop-wrong-item" });
                    return;
                }
                const left = (remainingBySku[item.sku] ?? 0) - 1;
                if (left < 0) {
                    playError(); flash("error");
                    showToast("error", t("drop_already_picked_qty"), { toastId: "drop-over-pick" });
                    return;
                }
                // Shared with the manual-pick modal — see
                // applyAcceptedUnits above.
                await applyAcceptedUnits(item, 1);
                return;
            }
        } catch (err) {
            console.error("drop submitScan error", err);
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-net-err" });
        } finally {
            setSubmitting(false);
            setScanValue("");
            // Drain one queued scan. setTimeout(0) lets React commit the
            // state updates from this run so the next call reads fresh
            // phase / target / remainingBySku / lookaheadQueue.
            //
            // KEEP inFlightRef=true while there's a queued drain. If
            // we released it here, a fresh keydown landing as its own
            // macrotask BEFORE the setTimeout fires would see
            // inFlightRef=false, run immediately, and jump ahead of
            // the queued scan — producing out-of-order processing.
            // The drain's setTimeout runs in one task that releases
            // the flag *and* invokes the next submitScan synchronously,
            // so no other JS can interleave between the release and
            // the next scan's start.
            const next = scanQueueRef.current.shift();
            if (next) {
                setTimeout(() => {
                    inFlightRef.current = false;
                    submitScanRef.current(next);
                }, 0);
            } else {
                inFlightRef.current = false;
            }
        }
    }, [scanValue, phase, module, t, flash, target, toteId, remainingBySku, filters, walkOrder,
        toteUnits, lastDropLocation, advanceToNextLocation, handleResume, handleTakeover,
        handleReopen, handleSwapConfirm, handleCloseTote, applyAcceptedUnits, performToteSwap,
        updateClosePrompt, updateMovePrompt, missing, lookupActiveSession]);

    // Keep submitScanRef pointed at the latest closure so the `finally`
    // drain recurses into the current dependencies, not a stale snapshot.
    useEffect(() => { submitScanRef.current = submitScan; }, [submitScan]);

    // ─── Manual pick ("barcode not scanning") ─────────────────────
    // The hand button on PickingScreen's action bar opens the modal
    // for the HERO item only; confirming applies the quantity through
    // the same accept path as that many scans. applyAcceptedUnits sends
    // that quantity in one /drop/scan-item commit, avoiding double-counts.
    const [manualPickOpen, setManualPickOpen] = useState(false);
    const [manualBusy, setManualBusy] = useState(false);
    const confirmManualDrop = useCallback(async (qty: number) => {
        if (manualBusy) return;
        if (phase !== "awaiting-items" || !target) { setManualPickOpen(false); return; }
        // Re-derive the hero item (same rule as the render: first item
        // with remaining units) — the modal was opened for it.
        const item = target.items.find(it => (remainingBySku[it.sku] ?? 0) > 0);
        if (!item) { setManualPickOpen(false); return; }
        const remaining = remainingBySku[item.sku] ?? 0;
        if (qty < 1 || qty > remaining) {
            playError(); flash("error");
            showToast("error", t("picking_manual_qty_invalid"), { toastId: "drop-manual-qty" });
            setManualPickOpen(false);
            return;
        }
        // A scan is still settling (rare — the scanner is disabled
        // while the modal is open). Leave the modal up so the picker
        // can just tap confirm again.
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setManualBusy(true);
        setSubmitting(true);
        try {
            await applyAcceptedUnits(item, qty);
        } catch (err) {
            console.error("drop manual pick error", err);
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-manual-err" });
        } finally {
            setManualBusy(false);
            setManualPickOpen(false);
            setSubmitting(false);
            // Same drain contract as submitScan's finally — a scan
            // queued while we held the flag must not be dropped.
            const next = scanQueueRef.current.shift();
            if (next) {
                setTimeout(() => {
                    inFlightRef.current = false;
                    submitScanRef.current(next);
                }, 0);
            } else {
                inFlightRef.current = false;
            }
        }
    }, [manualBusy, phase, target, remainingBySku, applyAcceptedUnits, flash, t]);

    // Drop any queued scans if the module wraps up so a late burst can't
    // fire scans against a no-longer-active drop session.
    useEffect(() => {
        if (phase === "complete") {
            scanQueueRef.current = [];
        }
    }, [phase]);

    // Cancel outstanding reservations on filter change and re-fetch
    // the next target so the picker isn't sent to a location whose
    // items are now filtered out.
    const onFiltersChange = useCallback(async (next: DropFilters) => {
        setFilters(next);
        // The queued lookahead was computed under the previous filters
        // — drop it so we don't show a location that no longer
        // matches.
        setLookaheadQueue([]);
        // Cancel and refetch only if we have an active session. Pass
        // toteId so the BE can flip the tote back to 'available' if
        // we haven't actually committed any items to it yet — keeps
        // totes.status accurate for cross-module visibility.
        try {
            await apiFetch("/drop/cancel", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module, toteId }),
            });
        } catch { /* ignore */ }
        if (
            phase === "awaiting-location" ||
            phase === "awaiting-items"
        ) {
            await advanceToNextLocation(walkOrder, next);
        } else if (phase === "complete") {
            // From "ALL CAUGHT UP" the picker may change filters
            // hoping to find work in another bucket.
            if (walkOrder !== null) {
                // They had already scanned a starting location, so the
                // walk order is anchored — re-run the advance so the
                // page recovers into awaiting-location when the new
                // filter has items (or stays on complete if it doesn't).
                await advanceToNextLocation(walkOrder, next);
            } else {
                // They hit "ALL CAUGHT UP" before ever scanning a
                // starting location (e.g. the first filter they tried
                // had no work). Don't fling them to a location they
                // never anchored to — send them back to scan their
                // starting location so the walk order is correct. The
                // pre-warm effect re-runs on this phase/filter change
                // and will re-detect "no work" if the new filter is
                // also empty.
                setPhase("awaiting-starting-location");
            }
        }
    }, [module, phase, walkOrder, advanceToNextLocation, toteId]);

    // "Missing" — the stock the picker was sent here to collect isn't
    // physically at the location. Opens the missing-item count flow: the
    // BE releases this stop's reserved claims and opens a real stock check
    // (origin='drop_missing'), then the picker proves the location (gate),
    // counts it, and completion corrects the inventory records so
    // /drop/next stops routing everyone back to the phantom stock.
    //
    // Degrades to the legacy skip (release claims, advance) when the check
    // can't be opened — the picker is never blocked by this feature.
    const handleDropMissing = useCallback(async () => {
        if (submitting) return;
        if (phase !== "awaiting-location" && phase !== "awaiting-items") return;
        if (!target) return;
        // The hero item is what sent them into the flow — first item with
        // units still owed (falls back to the first item when everything
        // is somehow satisfied).
        const hero = target.items.find(i => (remainingBySku[i.sku] ?? 0) > 0)
            ?? target.items[0] ?? null;
        setSubmitting(true);
        try {
            let body: {
                ok?: boolean;
                reason?: string;
                claims_cancelled?: boolean;
                stock_check_id?: number;
                location_display_name?: string | null;
                stock_check_lines?: StockCheckLine[];
                took_over_from?: string | null;
            } | null = null;
            try {
                const res = await apiFetch("/drop/missing/start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        module,
                        location: target.location,
                        sku: hero?.sku ?? null,
                    }),
                });
                body = await res.json();
            } catch { body = null; }

            if (!body?.ok || !body.stock_check_id) {
                // Check couldn't open (location locked by a race, no active
                // session, older BE, network error). claims_cancelled tells
                // us the BE already released this stop; otherwise fall back
                // to the legacy /drop/cancel so the reservation never
                // lingers. Either way: skip to the next location, exactly
                // what the flag button always did.
                if (!body?.claims_cancelled) {
                    try {
                        await apiFetch("/drop/cancel", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ module, toteId }),
                        });
                    } catch { /* ignore — advance below will still try */ }
                }
                flash("success");
                await advanceToNextLocation(walkOrder, filters);
                return;
            }

            if (body.took_over_from) {
                showToast("info", t("picking_missing_took_over", { name: body.took_over_from }),
                    { toastId: "drop-missing-tookover" });
            }
            const state: DropMissingState = {
                targetSku: hero?.sku ?? "",
                targetQty: hero ? (remainingBySku[hero.sku] ?? hero.qty) : 0,
                location: target.location,
                locationDisplayName: body.location_display_name
                    || target.locationDisplay || target.location,
                checkId: Number(body.stock_check_id),
                lines: body.stock_check_lines ?? [],
            };
            // Refs first (synchronously) so a scan arriving before React
            // commits this render is already intercepted by the flow.
            missingRef.current = state;
            missingStageRef.current = "gate";
            setMissing(state);
            setPhase("missing-location");
        } finally {
            setSubmitting(false);
        }
    }, [submitting, phase, target, module, walkOrder, filters, advanceToNextLocation, flash,
        toteId, remainingBySku, t]);

    // Cancel out of the count (gate or counting screen). The BE released
    // this stop's claims at /start, so cancelling is the legacy skip: move
    // on. The check row stays in_progress and is resumed (counts zeroed) if
    // the same session re-flags this location — same rule as picking.
    const cancelMissingFlow = useCallback(async () => {
        if (submitting) return;
        missingRef.current = null;
        missingStageRef.current = null;
        setMissing(null);
        setSurplusPrompt(null);
        setFinishConfirm(false);
        setSubmitting(true);
        try {
            flash("success");
            // Skip the preloaded queue — it was ranked before this stop's
            // claims were released.
            await advanceToNextLocation(walkOrder, filters, true);
        } finally {
            setSubmitting(false);
        }
    }, [submitting, walkOrder, filters, advanceToNextLocation, flash]);

    // Book a surplus find into the location, on the picker's explicit
    // say-so. Routed through the Stock Check module's own /stock-check/add
    // (same as picking's missing flow) so the unit is costed from the
    // catalogue and recorded on the check's lines.
    const confirmSurplusAdd = useCallback(async () => {
        if (!surplusPrompt || !missing) { setSurplusPrompt(null); return; }
        setAddingSurplus(true);
        try {
            const res = await apiFetch("/stock-check/add", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkId: missing.checkId,
                    sku: surplusPrompt.sku,
                    qty: 1,
                }),
            });
            if (!res.ok) {
                showToast("error", t("server_error"), { toastId: "drop-surplus-add" });
                return;
            }
            playBoxAdd();
            const applyAdd = (prev: DropMissingState): DropMissingState => {
                const hit = prev.lines.some(l => l.sku === surplusPrompt.sku);
                return {
                    ...prev,
                    lines: hit
                        ? prev.lines.map(l => l.sku === surplusPrompt.sku
                            ? { ...l, scanned_qty: l.scanned_qty + 1 }
                            : l)
                        : [...prev.lines, {
                            sku: surplusPrompt.sku,
                            title: surplusPrompt.sku,
                            barcode: surplusPrompt.barcode || null,
                            expected_qty: 0,
                            scanned_qty: 1,
                        }],
                };
            };
            if (missingRef.current) missingRef.current = applyAdd(missingRef.current);
            setMissing(prev => prev ? applyAdd(prev) : prev);
            setSurplusPrompt(null);
        } catch (err) {
            console.error("drop surplus add error", err);
            showToast("error", t("server_error"), { toastId: "drop-surplus-add" });
        } finally {
            setAddingSurplus(false);
        }
    }, [surplusPrompt, missing, t]);

    // Complete the count. The BE write-offs the unallocated shortfall for
    // the SKUs the picker engaged with (all lines when removeUncounted),
    // then the outcome card states what the count established.
    const finishMissingFlow = useCallback(async (removeUncounted: boolean) => {
        if (submitting || !missing) return;
        setSubmitting(true);
        try {
            const res = await apiFetch("/drop/missing/finish", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    module,
                    checkId: missing.checkId,
                    targetSku: missing.targetSku || null,
                    removeUncounted,
                    location: missing.location,
                }),
            });
            const body = await res.json();
            if (!body?.ok) {
                playError(); flash("error");
                showToast("error", t("server_error"), { toastId: "drop-missing-finish" });
                return;
            }
            const liveLines = missingRef.current?.lines ?? missing.lines;
            const foundQty = liveLines.find(l => l.sku === missing.targetSku)?.scanned_qty ?? 0;
            missingRef.current = null;
            missingStageRef.current = null;
            setFinishConfirm(false);
            setResolved({
                sku: missing.targetSku,
                foundQty,
                requiredQty: missing.targetQty,
                writtenOff: Number(body.summary?.writtenOff ?? 0),
            });
            setMissing(null);
            setSurplusPrompt(null);
            setPhase("missing-resolved");
        } catch (err) {
            console.error("drop missing finish error", err);
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "drop-missing-finish" });
        } finally {
            setSubmitting(false);
        }
    }, [submitting, missing, module, flash, t]);

    // Leave the outcome card. A fresh /drop/next reflects the corrected
    // inventory: a written-off location drops off the route; one where the
    // count found the stock is re-offered.
    const continueFromResolution = useCallback(async () => {
        if (submitting) return;
        setResolved(null);
        setSubmitting(true);
        try {
            await advanceToNextLocation(walkOrder, filters, true);
        } finally {
            setSubmitting(false);
        }
    }, [submitting, walkOrder, filters, advanceToNextLocation]);

    // Missing-item flow derived values (mirrors picking's ActiveWaveScreen).
    const inMissingFlow = phase === "missing-location"
        || phase === "missing-counting"
        || phase === "missing-resolved";
    // The count has turned up everything the flagged item still owed.
    const missingTargetFound = !!missing
        && missing.targetQty > 0
        && (missing.lines.find(l => l.sku === missing.targetSku)?.scanned_qty ?? 0)
            >= missing.targetQty;
    // Other SKUs at this location the count did not account for. Reserved
    // units are excluded — unscanned does not mean lost for those — and so
    // is the target SKU, which the flow always reconciles.
    const uncountedLines: UncountedLine[] = (missing?.lines ?? [])
        .filter(l => l.sku !== missing?.targetSku)
        .map(l => ({
            sku: l.sku,
            title: l.title,
            qty: Math.max(0, (l.expected_qty - l.scanned_qty) - Number(l.allocated_qty ?? 0)),
        }))
        .filter(l => l.qty > 0);

    // Sub-prompt copy per phase.
    const subPrompt = (() => {
        if (phase === "awaiting-starting-location") return t("drop_scan_current_location");
        if (phase === "awaiting-tote") return t("drop_scan_empty_tote");
        if (phase === "awaiting-location") return t("drop_scan_target_location");
        if (phase === "awaiting-items") {
            const totalLeft = Object.values(remainingBySku).reduce((s, n) => s + n, 0);
            return totalLeft === 1
                ? t("drop_scan_items_one")
                : t("drop_scan_items", { qty: String(totalLeft) });
        }
        return "";
    })();

    // ─── render ──────────────────────────────────────────────────
    if (view === "put-away") {
        return (
            <DropPutAwayView
                module={module}
                titleKey={titleKey}
                putAwayCount={putAwayCount}
                itemsCount={itemsCount}
                onBack={() => setView("scan")}
                onCounterChange={setPutAwayCount}
            />
        );
    }

    // First /drop/next hasn't resolved yet — we don't know whether the
    // module has work. Show the same FlowLoader the wave-start screen
    // uses and wait, rather than flashing the scan/picking hero before
    // it flips to "ALL CAUGHT UP". `initialized` is set true the moment
    // the mount preload resolves (or errors), so this only covers the
    // brief determine-the-screen window.
    if (!initialized || !sessionChecked) {
        // Same centred FlowLoader the picking page uses on wave start.
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 56px)" }}>
                <FlowLoader />
            </div>
        );
    }

    const showFilters = module === "shelf";
    const resumeItemCount = resumePrompt ? getDropSessionUnitCount(resumePrompt) : 0;
    // The hero item: the first item at the target with units still to drop.
    //
    // Once every item is satisfied we do NOT return null — we PIN the last
    // item with left:0 instead (mirroring picking's ActiveWaveScreen, which
    // holds the finished line at qty_picked === qty_required rather than
    // blanking the hero). The reason is the location-change takeover: the
    // optimistic decrement in applyAcceptedUnits commits to state BEFORE the
    // /drop/scan-item round-trip, and the await flushes that commit, so the
    // final unit at a location paints one render with target still on the OLD
    // location and every remaining count at zero. Returning null there flipped
    // useUnifiedScreen false and UNMOUNTED PickingScreen; the remount reset its
    // prevLocationRef to null, and its location-change effect then swallowed
    // the takeover on the `prev === null` first-mount guard — at every single
    // location. Pinning keeps one instance mounted across that window so the
    // location prop genuinely changes under a live ref and the takeover fires.
    const currentItem = (() => {
        if (!target || target.items.length === 0) return null;
        for (const it of target.items) {
            const left = remainingBySku[it.sku] ?? 0;
            if (left > 0) return { ...it, left };
        }
        // All satisfied — hold the hero the picker was just looking at (by SKU),
        // so the pinned card keeps the same product image/barcode rather than
        // flipping to a different SKU for the round-trip. Falls back to the last
        // item if that SKU has since left the target.
        const held = target.items.find(it => it.sku === heroSkuRef.current)
            ?? target.items[target.items.length - 1];
        return { ...held, left: 0 };
    })();
    // Remember the SKU actually rendered as the hero, for the pin above.
    if (currentItem && currentItem.left > 0) heroSkuRef.current = currentItem.sku;

    // Active-pick chrome (progress bar + header bar) replaces the
    // page-title-bar + filter pills once the picker has scanned their
    // starting location. Bar + counter use the shared module-wide
    // `progress` so multiple pickers see the same totals and contribute
    // to one bar — see drop-progress.js on the BE.
    const isActivePick = phase === "awaiting-tote"
                      || phase === "awaiting-location"
                      || phase === "awaiting-items";
    const progressPct = progress.total > 0 ? (progress.picked / progress.total) * 100 : 0;

    // Both drop modules suppress the ps-bar (progress bar + pick header)
    // until the picker has scanned BOTH a starting location and a tote.
    // Until then they show the lightweight page-title chrome (title +
    // put-away toggle + filters) so the opening screen stays uncluttered;
    // the ps-bar appears once they're actually walking to a target (the
    // awaiting-location / awaiting-items phases, rendered by PickingScreen).
    // Shelf and Forecast share this one chrome path — kept identical so
    // their styling never needs adjusting independently.
    const showTitleChrome =
        phase === "complete"
        || phase === "awaiting-starting-location"
        || phase === "awaiting-tote";

    // Use the unified PickingScreen for the active-pick phases where
    // there's a real target + item to render. Falls through to the
    // legacy simplified renders for awaiting-starting-location,
    // awaiting-tote, and complete (each of those still uses the
    // original chrome since the picker isn't on a pick yet).
    // NOTE: `currentItem` is now non-null for any target that has items (it
    // pins a satisfied hero at left:0 rather than going null — see above), so
    // this gate no longer flickers false during the zero-remaining window
    // between the last accepted scan and the target swap. Keep the
    // !!currentItem term anyway: it still guards a target with an empty
    // items[] array, which the JSX below cannot render.
    const useUnifiedScreen = isActivePick && !!target && !!currentItem;
    // "Other SKUs at this location" — every other item at the drop
    // target that still has remaining qty > 0. Sorted to put any with
    // qty before any with none (defensive — items[] only contains
    // entries with qty>0 anyway).
    const otherDropItems = target
        ? target.items
            .filter(it => it.sku !== currentItem?.sku && (remainingBySku[it.sku] ?? it.qty) > 0)
            .map(it => ({
                sku: it.sku,
                title: it.productName ?? null,
                thumbnail_url: it.productThumbnailUrl ?? it.productImageUrl ?? null,
                barcode: it.productBarcode ?? null,
                qty_required: remainingBySku[it.sku] ?? it.qty,
            }))
        : [];
    // Location-level unit counter for the "Units at this location"
    // panel head — total units to drop at this target across ALL SKUs
    // vs how many are already dropped. Derived from the live
    // remainingBySku map, so it moves on every accepted item scan,
    // sibling SKUs included — never from the hero item alone.
    const locationUnitsTotal = target
        ? target.items.reduce((s, it) => s + it.qty, 0)
        : 0;
    const locationUnitsDropped = target
        ? target.items.reduce(
            (s, it) => s + Math.max(0, it.qty - (remainingBySku[it.sku] ?? it.qty)), 0)
        : 0;

    // Demo panel — phase-aware simulate-a-scan buttons. Reused both
    // inside PickingScreen's aboveActionBar slot (for unified phases,
    // so it sits between the bay diagram and the dark action bar) and
    // below the simplified hero for legacy phases.
    const demoPanel = (demo && phase !== "complete") ? (
        <div className="book-in-demo-panel">
            <span className="book-in-demo-label">Demo</span>

            {phase === "awaiting-starting-location" && (
                <>
                    {DEMO_LOW_LOCATIONS.map(loc => (
                        <button
                            key={loc}
                            type="button"
                            className="book-in-demo-btn"
                            onClick={() => submitScan(loc)}
                            title="Scan this as your starting location"
                        >
                            {loc}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-LOC")}
                        title="Should be rejected as invalid_starting_location"
                    >
                        wrong
                    </button>
                </>
            )}

            {phase === "awaiting-tote" && (
                <>
                    {DEMO_TOTES.map(t => (
                        <button
                            key={t}
                            type="button"
                            className="book-in-demo-btn book-in-demo-btn--tote"
                            onClick={() => submitScan(t)}
                        >
                            {t}
                        </button>
                    ))}
                </>
            )}

            {phase === "awaiting-location" && target && (
                <>
                    <button
                        type="button"
                        className="book-in-demo-btn"
                        onClick={() => submitScan(target.location)}
                    >
                        {target.location}
                    </button>
                    {DEMO_HIGH_LOCATIONS.filter(l => l !== target.location).map(l => (
                        <button
                            key={l}
                            type="button"
                            className="book-in-demo-btn book-in-demo-btn--bad"
                            onClick={() => submitScan(l)}
                            title="Wrong shelf-drop reserve — should reject"
                        >
                            {l}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-LOC")}
                        title="Scan a wrong location"
                    >
                        wrong
                    </button>
                    {DEMO_TOTES.filter(dt => dt !== toteId).slice(0, 2).map(dt => (
                        <button
                            key={dt}
                            type="button"
                            className="book-in-demo-btn book-in-demo-btn--tote"
                            onClick={() => submitScan(dt)}
                            title="Tote full — swap to this tote"
                        >
                            {dt}
                        </button>
                    ))}
                </>
            )}

            {phase === "missing-location" && missing && (
                <button
                    type="button"
                    className="book-in-demo-btn"
                    onClick={() => submitScan(missing.location)}
                    title="Scan the flagged location"
                >
                    {missing.location}
                </button>
            )}

            {phase === "missing-counting" && missing && (
                <>
                    {missing.lines.slice(0, 4).map(l => (
                        <button
                            key={l.sku}
                            type="button"
                            className="book-in-demo-btn"
                            onClick={() => submitScan(l.barcode || l.sku)}
                            title={`Count one ${l.sku}`}
                        >
                            {l.sku}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-SKU")}
                        title="Unknown barcode"
                    >
                        wrong
                    </button>
                </>
            )}

            {phase === "awaiting-items" && currentItem && (
                <>
                    {currentItem.productBarcode && (
                        <button
                            type="button"
                            className="book-in-demo-btn"
                            onClick={() => submitScan(currentItem.productBarcode as string)}
                            title={`Product barcode for ${currentItem.sku}`}
                        >
                            {currentItem.productBarcode}
                        </button>
                    )}
                    <button
                        type="button"
                        className="book-in-demo-btn"
                        onClick={() => submitScan(currentItem.sku)}
                        title="Scan by SKU"
                    >
                        {currentItem.sku}
                    </button>
                    <button
                        type="button"
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-SKU")}
                        title="Scan a wrong SKU"
                    >
                        wrong
                    </button>
                    {DEMO_TOTES.filter(dt => dt !== toteId).slice(0, 2).map(dt => (
                        <button
                            key={dt}
                            type="button"
                            className="book-in-demo-btn book-in-demo-btn--tote"
                            onClick={() => submitScan(dt)}
                            title="Tote full — swap to this tote"
                        >
                            {dt}
                        </button>
                    ))}
                </>
            )}
        </div>
    ) : null;

    return (
        // During the active pick the unified PickingScreen is the only
        // thing on screen (no title chrome), so drop the .drop-page side
        // padding and let it bleed full-width — same as the picking page,
        // where PickingScreen sits in a no-padding .scan-panel.picking-
        // active. This makes the .ps-progress bar span edge-to-edge here
        // too; PickingScreen supplies its own internal content padding.
        <div className={"scan-panel picking-active drop-page" + (useUnifiedScreen ? " drop-page--full-bleed" : "")}>
            {/* Top chrome (identical for Shelf + Forecast).
                  • Title chrome (page-title-bar + put-away toggle +
                    filter pills): the "complete" resting state plus the
                    pre-pick scan-starting-location and scan-tote phases,
                    so the opening screen stays uncluttered and the picker
                    can set filters before starting. See `showTitleChrome`.
                  • ps-bar (progress + back button): any active phase
                    where the unified PickingScreen isn't yet rendering a
                    target. PickingScreen's own PickingTopBar covers the
                    awaiting-location + awaiting-items phases once a
                    target + item exist. */}
            {showTitleChrome ? (
                <>
                    <div className="page-title-bar picking-selector-header">
                        <h1 className="page-title">{t(titleKey)}</h1>
                        <DropViewToggle view="scan" putAwayCount={putAwayCount} itemsCount={itemsCount} onChange={setView} />
                        <button className="page-back-btn" onClick={() => navigate("/pick-face")} aria-label="Back to Pick Face">
                            <ArrowLeft size={20} />
                        </button>
                    </div>
                    {showFilters && initialized
                        && (phase !== "complete"
                            || itemsCount > 0
                            || filters.channel !== "all"
                            || filters.age !== "all"
                            || filters.due === true) && (
                        // Stay visible on "ALL CAUGHT UP" when the count
                        // still shows work OR any filter is applied.
                        // queue-count now carries the picker's filter, so
                        // a drained filter reads 0 — without the filter
                        // check the pills would vanish and leave no way
                        // to clear/change it.
                        <DropFilterPills value={filters} onChange={onFiltersChange} showDue={afterCutoff} />
                    )}
                </>
            ) : (isActivePick || inMissingFlow) && !useUnifiedScreen ? (
                <PickingTopBar
                    itemsTotal={progress.total}
                    itemsPicked={progress.picked}
                    barExtras={toteId ? (
                        <span className="picking-open-tote" title={t("picking_open_tote_label")}>
                            <PackageOpen size={16} className="picking-open-tote-icon" />
                            <span className="picking-open-tote-id">{toteId}</span>
                        </span>
                    ) : undefined}
                    onAlert={(phase === "awaiting-location" || phase === "awaiting-items") ? handleDropMissing : undefined}
                    onBack={() => navigate("/pick-face")}
                    alertDisabled={submitting || !currentItem}
                />
            ) : null}

            {aisleModalOpen && (
                // Units-by-aisle popup, opened from the ⓘ above the scan
                // hero's glyph. Overlay tap or ✕ closes; rows are
                // BE-sorted alphabetically (AA after Z) and sum to the
                // filtered units badge.
                <div className="modal-overlay book-in-modal-overlay" onClick={() => setAisleModalOpen(false)}>
                    <div className="modal-card book-in-reopen-modal drop-aisles-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_aisles_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setAisleModalOpen(false)}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            {aisleRows === null ? (
                                <FlowLoader />
                            ) : aisleRows.length === 0 ? (
                                <p className="drop-aisles-empty">{t("drop_aisles_empty")}</p>
                            ) : (
                                <>
                                    <p className="drop-aisles-hint">{t("drop_aisles_hint")}</p>
                                    <div className="drop-aisles-list">
                                        {aisleRows.map((r) => (
                                            <div className="drop-aisles-row" key={r.aisle ?? "__other"}>
                                                <span className="drop-aisles-aisle">
                                                    {r.aisle !== null
                                                        ? t("drop_aisles_aisle", { aisle: r.aisle })
                                                        : t("drop_aisles_other")}
                                                </span>
                                                <span className="drop-aisles-units">{r.units}</span>
                                            </div>
                                        ))}
                                        <div className="drop-aisles-row drop-aisles-row--total">
                                            <span className="drop-aisles-aisle">{t("drop_aisles_total")}</span>
                                            <span className="drop-aisles-units">
                                                {aisleRows.reduce((s, r) => s + r.units, 0)}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="scan-panel picking-active" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {phase === "missing-resolved" && resolved ? (
                    /* Outcome of the count. Dismissed by the picker, not a
                       timer — the correction should not be missable. */
                    <div className="modal-overlay book-in-modal-overlay">
                        <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>
                                    {resolved.foundQty >= resolved.requiredQty && resolved.requiredQty > 0
                                        ? t("drop_missing_res_found_title")
                                        : t("drop_missing_res_short_title")}
                                </h2>
                            </div>
                            <div className="modal-card-body">
                                <p>
                                    {resolved.foundQty >= resolved.requiredQty && resolved.requiredQty > 0
                                        ? t("drop_missing_res_found_body", {
                                            count: String(resolved.foundQty), sku: resolved.sku,
                                        })
                                        : t("drop_missing_res_short_body", {
                                            count: String(resolved.foundQty),
                                            required: String(resolved.requiredQty),
                                            sku: resolved.sku,
                                        })}
                                </p>
                                {resolved.writtenOff > 0 && (
                                    <p className="stock-check-modal-hint">
                                        {t("drop_missing_res_written_off", {
                                            count: String(resolved.writtenOff),
                                        })}
                                    </p>
                                )}
                                <div className="modal-confirm-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-success btn-lg"
                                        onClick={continueFromResolution}
                                        disabled={submitting}
                                    >
                                        {t("drop_missing_res_continue")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : phase === "missing-location" && missing ? (
                    /* Gate: prove they're at the flagged shelf before a
                       single item is counted. */
                    <MissingLocationGate
                        location={missing.location}
                        locationDisplayName={missing.locationDisplayName}
                        sku={missing.targetSku}
                        qtyOutstanding={missing.targetQty}
                        flashHidden={scanStatus === "error"}
                        busy={submitting}
                        onCancel={cancelMissingFlow}
                    />
                ) : phase === "missing-counting" && missing ? (
                    <div className={"picking-missing-screen" + (scanStatus === "error" ? " scan-flash-hidden" : "")}>
                        <MissingItemCountList
                            targetSku={missing.targetSku}
                            location={missing.location}
                            locationDisplayName={missing.locationDisplayName}
                            expected={[]}
                            counts={{}}
                            stockCheckLines={missing.lines}
                        />
                        <div className="picking-sub-prompt">{t("picking_missing_cmd_scan_items")}</div>
                        <div className="picking-missing-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={cancelMissingFlow}
                                disabled={submitting}
                            >
                                {t("picking_missing_cancel")}
                            </button>
                            <button
                                type="button"
                                className={"btn " + (missingTargetFound ? "btn-success" : "btn-primary")}
                                onClick={() => setFinishConfirm(true)}
                                disabled={submitting}
                            >
                                {submitting
                                    ? t("picking_missing_finishing")
                                    : missingTargetFound
                                        ? t("picking_missing_done_found")
                                        : t("picking_missing_done")}
                            </button>
                        </div>

                        {/* Finish confirm — what the count found, and the
                            "remove the uncounted rest?" decision. Same
                            component as picking's missing flow. */}
                        {finishConfirm && (
                            <MissingFinishConfirm
                                location={missing.locationDisplayName || missing.location}
                                sku={missing.targetSku}
                                foundQty={missing.lines.find(l => l.sku === missing.targetSku)?.scanned_qty ?? 0}
                                requiredQty={missing.targetQty}
                                uncounted={uncountedLines}
                                busy={submitting}
                                onCancel={() => setFinishConfirm(false)}
                                onConfirm={finishMissingFlow}
                            />
                        )}

                        {/* Surplus found during the count — booking in is the
                            picker's decision, via the Stock Check module's
                            Add so the unit is costed from the catalogue. */}
                        {surplusPrompt && (
                            <div className="modal-overlay" onClick={() => !addingSurplus && setSurplusPrompt(null)}>
                                <div className="modal-card" onClick={e => e.stopPropagation()}>
                                    <div className="modal-card-header">
                                        <h2>{t(surplusPrompt.extraUnit
                                            ? "picking_missing_extra_unit_title"
                                            : "picking_missing_surplus_title")}</h2>
                                    </div>
                                    <div className="modal-card-body">
                                        <p className="stock-check-modal-hint">
                                            {t(surplusPrompt.extraUnit
                                                ? "picking_missing_extra_unit_body"
                                                : "picking_missing_surplus_body", { sku: surplusPrompt.sku })}
                                        </p>
                                        <div className="modal-confirm-buttons">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-lg"
                                                onClick={() => setSurplusPrompt(null)}
                                                disabled={addingSurplus}
                                            >
                                                {t("no")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-success btn-lg"
                                                onClick={confirmSurplusAdd}
                                                disabled={addingSurplus}
                                            >
                                                {addingSurplus ? t("submitting") : t("yes")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : useUnifiedScreen && target && currentItem ? (
                    <PickingScreen
                        priority={null}
                        orderRef={toteId ? `Tote ${toteId}` : t(titleKey)}
                        itemsTotal={progress.total}
                        itemsPicked={progress.picked}
                        location={target.locationDisplay ?? target.location}
                        locationHighLow={target.locationHighLow ?? null}
                        bayPosition={target.bayPosition ?? null}
                        sku={currentItem.sku}
                        productTitle={currentItem.productName ?? null}
                        productBarcode={currentItem.productBarcode ?? null}
                        productBarcodes={currentItem.barcodes ?? null}
                        productImageUrl={currentItem.productImageUrl ?? null}
                        productThumbnailUrl={currentItem.productThumbnailUrl ?? null}
                        qtyTotal={currentItem.qty}
                        qtyPicked={Math.max(0, currentItem.qty - currentItem.left)}
                        showProductImage={showProductImage}
                        otherSkusToShow={otherSkusCount}
                        otherSkusAtLocation={otherDropItems}
                        locationQtyTotal={locationUnitsTotal}
                        locationQtyPicked={locationUnitsDropped}
                        locationSkuCount={target.items.length}
                        locationChangeMs={locationChangeMs}
                        newLocationHighlightMs={newLocationHighlightMs}
                        suppressNextTransition={suppressTransition}
                        pickSpeed={pickSpeed}
                    cycle={cycle}
                        scanTarget={
                            phase === "awaiting-location" && requireLocScan
                                ? "location"
                                : "barcode"
                        }
                        lastPicked={lastDropped}
                        lastPickedLabel={t("drop_last_dropped_label")}
                        flashActive={scanStatus === "error"}
                        onAlert={handleDropMissing}
                        onBack={() => navigate("/pick-face")}
                        alertDisabled={submitting || !currentItem}
                        onLocationTap={(phase === "awaiting-location" || phase === "awaiting-items")
                            ? () => updateMovePrompt({ open: true })
                            : undefined}
                        locationTapDisabled={submitting}
                        onManualPick={phase === "awaiting-items" ? () => setManualPickOpen(true) : undefined}
                        // Disabled while the hero is pinned at left:0 (every item
                        // satisfied, pick-complete in flight) — nothing is left to
                        // pick by hand here and the modal would open a zero-qty line.
                        manualPickDisabled={submitting || manualBusy || currentItem.left === 0}
                        barExtras={toteId ? (
                            <span className="picking-open-tote" title={t("picking_open_tote_label")}>
                                <PackageOpen size={16} className="picking-open-tote-icon" />
                                <span className="picking-open-tote-id">{toteId}</span>
                            </span>
                        ) : undefined}
                        aboveActionBar={demoPanel}
                    />
                ) : phase === "complete" ? (
                    <div className="picking-complete">
                        {/* Shared positioning slot — keeps the amber
                            tick and the "ALL CAUGHT UP" message anchored
                            at the same vertical position so the swap
                            between them is a fade-in, not a re-layout. */}
                        <div className="drop-complete-stage">
                            {completeStage === "tick" ? (
                                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                            ) : (
                                <div className="drop-all-caught-up">
                                    {t("drop_all_caught_up")}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (resuming && phase === "awaiting-starting-location") ? (
                    /* Resume chosen, next target still loading. `phase` is
                       the mount default here, whose hero would read "SCAN
                       CURRENT LOCATION" — an instruction the picker follows,
                       and which used to start a NEW session over the one
                       they just resumed (2026-10-13 tote 3818). Hold on a
                       loader until the resumed target lands; submitScan
                       rejects anything scanned meanwhile. */
                    <div
                        className="drop-resume-loading"
                        role="status"
                        aria-label={t("drop_resuming")}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "40vh" }}
                    >
                        <FlowLoader />
                    </div>
                ) : (phase === "awaiting-starting-location" || phase === "awaiting-tote") ? (
                    /* The "is there work?" determination already happened
                       behind the FlowLoader (see the !initialized early
                       return above), so by the time we render here we
                       KNOW there's work and can show the scan prompt
                       immediately — no flash before "ALL CAUGHT UP". */
                    <ScanPromptHero
                        prompt={subPrompt}
                        flashHidden={scanStatus === "error"}
                        // Units-by-aisle popup opener — sits directly above
                        // the scan glyph so it reads as info ABOUT the work
                        // the prompt is sending the picker into.
                        aboveIcon={(
                            <button
                                type="button"
                                className="drop-aisles-btn"
                                onClick={openAisleModal}
                                title={t("drop_aisles_title")}
                                aria-label={t("drop_aisles_title")}
                            >
                                <Info size={22} />
                            </button>
                        )}
                    >
                        <div className="picking-hero-ticks">
                            {/* Per-scan green tick suppressed — audio cue
                                confirms accepted scans. Errors render via
                                the shared ScanFlashOverlay; the amber
                                "all caught up" tick still shows here. */}
                            <ScanStatusIcons status={scanStatus === "complete" ? "complete" : "idle"} />
                        </div>
                        {/* Right after a tote close: remind the picker
                            where their last item came from, so they can
                            walk back and anchor there. Gone once a new
                            starting location is scanned. */}
                        {phase === "awaiting-starting-location" && postCloseLastLocation && (
                            <div className="drop-close-last-loc drop-close-last-loc--hero">
                                <MapPin size={16} aria-hidden="true" />
                                <span>
                                    {t("drop_close_last_location", { location: postCloseLastLocation })}
                                </span>
                            </div>
                        )}
                    </ScanPromptHero>
                ) : (
                    /* Transient gap between phases. We pre-warm the next
                       target on mount + after the starting-location scan,
                       so this fallback is reachable only on the very
                       first frame before the preload returns. No text —
                       just the scan glyph — so the picker doesn't read
                       a "loading" message that flashes for 100ms. */
                    <ScanPromptHero prompt="" flashHidden={scanStatus === "error"} />
                )}

                {/* Bad-scan feedback — full-viewport red wash + X above
                    every layout layer, shared across all five picking
                    workflows. Previously the unified-pick phase here had
                    NO error icon at all (just a background tint hidden
                    behind the hero cards + the audio cue). */}
                <ScanFlashOverlay active={scanStatus === "error"} />

                {/* Manual pick ("barcode not scanning") — confirm the
                    hero item's quantity without a scan. The scanner is
                    gated below while it's open so a stray trigger pull
                    can't advance state under the modal. */}
                {manualPickOpen && currentItem && (
                    <ManualPickModal
                        // Keyed on the hero SKU so a target/hero swap
                        // under the open modal remounts it — the qty
                        // resets against the NEW item instead of
                        // silently retargeting.
                        key={`${target?.location ?? ""}:${currentItem.sku}`}
                        item={{
                            sku: currentItem.sku,
                            title: currentItem.productName ?? null,
                            thumbnailUrl: currentItem.productThumbnailUrl ?? null,
                            imageUrl: currentItem.productImageUrl ?? null,
                            maxQty: currentItem.left,
                        }}
                        busy={manualBusy}
                        onConfirm={confirmManualDrop}
                        onClose={() => setManualPickOpen(false)}
                    />
                )}

                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={submitScan}
                    // Deliberately NOT disabled on `submitting`. The
                    // global keydown listener short-circuits on
                    // `disabled`, which would drop fast burst scans
                    // while a previous one is mid-flight. submitScan's
                    // inFlightRef + scanQueueRef pair serialises the
                    // bursts instead — queued scans drain in the
                    // previous call's `finally`. `complete` still
                    // disables: nothing to scan in the resting state,
                    // and the manual-pick modal does too — a scan
                    // mid-confirm could advance the hero under the
                    // picker's decision.
                    // `closePrompt` keeps scanning alive too: the close-confirm
                    // is confirmed hands-free by rescanning the tote, and that
                    // prompt can be open while phase is "complete" (the module
                    // showed no more work) — which would otherwise swallow it.
                    disabled={(phase === "complete" && !resumePrompt && !closePrompt)
                        || manualPickOpen
                        // Count-flow modals: a stray trigger pull must not
                        // advance state under the picker's decision, and the
                        // outcome card has nothing to scan.
                        || surplusPrompt !== null
                        || finishConfirm
                        || phase === "missing-resolved"}
                    disableCamera
                    hideToggles
                />

                {/* Legacy phases (starting-location, awaiting-tote, transient
                    fallback) still render the simplified hero — show the demo
                    panel as a sibling here. For unified-screen phases the
                    panel is passed via aboveActionBar so it sits between the
                    bay diagram and the dark action bar (both visible). */}
                {!useUnifiedScreen && demoPanel}
            </div>

            {resumePrompt && (
                <div className="modal-overlay book-in-modal-overlay">
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_resume_title")}</h2>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">
                                    {resumePrompt.toteId ?? t("drop_resume_no_tote")}
                                </div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">
                                        {resumeItemCount}
                                    </span>
                                    <span className="book-in-close-card-count-label">
                                        {resumeItemCount === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {resumePrompt.target && resumePrompt.toteId
                                    ? t("drop_resume_intro", {
                                          tote: resumePrompt.toteId,
                                          items: resumeItemCount,
                                          location: resumePrompt.target.locationDisplay ?? resumePrompt.target.location,
                                      })
                                    : resumePrompt.toteId
                                        ? t("drop_resume_intro_no_location", {
                                              tote: resumePrompt.toteId,
                                              items: resumeItemCount,
                                          })
                                        : t("drop_resume_intro_no_tote", {
                                              items: resumeItemCount,
                                          })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                {/* Never "start over" — an operator at this modal may
                                    be holding stock they have physically taken off a
                                    shelf. With a tote, the only safe exit is to close
                                    it and send it to put-away (behind the rescan-or-
                                    confirm step); with no tote there is nothing
                                    physical to lose, so discarding is honest. */}
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={resumePrompt.toteId ? handleCloseToteFromResume : handleDiscardSession}
                                    disabled={resuming}
                                >
                                    {t(resumePrompt.toteId ? "drop_resume_close_tote" : "drop_resume_discard")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleResume}
                                    disabled={resuming}
                                >
                                    {t("drop_resume_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {takeoverPrompt && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => { if (!takingOver) setTakeoverPrompt(null); }}
                >
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_takeover_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={() => setTakeoverPrompt(null)}
                                disabled={takingOver}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <AlertTriangle size={32} />
                                </div>
                                <div className="book-in-close-card-id">{takeoverPrompt.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{takeoverPrompt.units}</span>
                                    <span className="book-in-close-card-count-label">
                                        {takeoverPrompt.units === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("drop_takeover_intro", {
                                    tote: takeoverPrompt.toteId,
                                    picker: takeoverPrompt.pickerName,
                                })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => setTakeoverPrompt(null)}
                                    disabled={takingOver}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleTakeover}
                                    disabled={takingOver}
                                >
                                    {t("drop_takeover_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reopen-closed-tote prompt. Shown when the scanned tote is
                'closed' (holds picked stock awaiting put-away). Confirm
                (button or rescan the same tote) reopens it keeping its
                contents; cancel returns to the scan-tote step. */}
            {reopenPrompt && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => { if (!reopening) setReopenPrompt(null); }}
                >
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_reopen_tote_title")}</h2>
                            <button className="modal-close" onClick={() => setReopenPrompt(null)} disabled={reopening}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">{reopenPrompt.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{reopenPrompt.units}</span>
                                    <span className="book-in-close-card-count-label">
                                        {reopenPrompt.units === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">{t("drop_reopen_tote_intro")}</p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => setReopenPrompt(null)}
                                    disabled={reopening}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleReopen}
                                    disabled={reopening}
                                >
                                    {t("drop_reopen_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm-swap prompt: scanning the same new tote again (or
                Confirm) switches to it; Cancel keeps the tote in hand. */}
            {swapPrompt && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => { if (!swapping) setSwapPrompt(null); }}
                >
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_swap_tote_title")}</h2>
                            <button className="modal-close" onClick={() => setSwapPrompt(null)} disabled={swapping}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">{swapPrompt.toteId}</div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("drop_swap_tote_intro", { tote: swapPrompt.toteId, current: toteId ?? "" })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => setSwapPrompt(null)}
                                    disabled={swapping}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleSwapConfirm}
                                    disabled={swapping}
                                >
                                    {t("drop_swap_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Move-to-location prompt. The destination is confirmed only by
                scanning it; cancelling leaves the active target untouched. */}
            {movePrompt && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => { if (!moving) updateMovePrompt(null); }}
                >
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_move_location_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={() => updateMovePrompt(null)}
                                disabled={moving}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <MapPin size={32} />
                                </div>
                                <div className="book-in-close-card-id">
                                    {target?.locationDisplay ?? target?.location}
                                </div>
                            </div>
                            <p className="book-in-close-hint">{t("drop_move_location_hint")}</p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => updateMovePrompt(null)}
                                    disabled={moving}
                                >
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Close-current-tote prompt. The first scan only opens this
                safety step; Confirm or a second scan of the same tote sends
                picked contents to put-away and ends the Drop session. */}
            {closePrompt && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => { if (!closing) dismissClosePrompt(); }}
                >
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("drop_close_tote_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={dismissClosePrompt}
                                disabled={closing}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">{closePrompt.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{closePrompt.units}</span>
                                    <span className="book-in-close-card-count-label">
                                        {closePrompt.units === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            {/* Where the picker last got an item — so they know
                                where they were when they pick up again. Hidden
                                when nothing has been picked yet. */}
                            {closePrompt.lastLocation && (
                                <div className="drop-close-last-loc">
                                    <MapPin size={14} aria-hidden="true" />
                                    <span>
                                        {t("drop_close_last_location", { location: closePrompt.lastLocation })}
                                    </span>
                                </div>
                            )}
                            <p className="book-in-close-hint">{t("drop_close_tote_intro")}</p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={dismissClosePrompt}
                                    disabled={closing}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleCloseTote}
                                    disabled={closing}
                                >
                                    {t("drop_close_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActiveDropScreen;
